// ============================================================================
// HybridFocusBoard.jsx - Phase 4.7 Safety Net for Sticky Footer
// ============================================================================
// WHERE TO PLACE: src/HybridFocusBoard.jsx
// ============================================================================
// Phase 4.7 Changes:
// A) Root container uses height:100% - relies on parent for sizing
// B) Sticky footer uses position:sticky with measured height
// C) Content area has paddingBottom equal to footer height
// D) Works in BOTH normal mode and fullscreen mode
// ============================================================================
// IMPORTANT: Parent container in App.js MUST provide:
//   - height: calc(100vh - 140px) or similar fixed height
//   - overflow: hidden
//   - The HFB middle region is the ONLY scroll container
// ============================================================================
// Previous Phase 4.6 changes preserved:
// A) Fix Scroll mode - native overflow:auto, no pan/zoom transforms
// B) Fix initial centering - auto-center on mount/resize/mode-toggle
// C) Sticky bottom lanes - Unassigned/Floating always visible at bottom
// D) Canvas mode auto-fit - scale-to-fit on initial render
// E) Remove blue droppable tint - neutral backgrounds only
// F) Fix team column droppable height - full height droppable areas
// ============================================================================

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  ChevronLeft, 
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Copy,
  AlertCircle,
  Loader2,
  AlertTriangle,
  Check,
  X,
  Lock,
  Calendar,
  Shield,
  Maximize2,
  Minimize2,
  Eye,
  EyeOff,
  Briefcase,
  User
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDroppable,
  useDraggable
} from '@dnd-kit/core';
import { 
  useHFBWeek,
  useHFBTeamAssignments,
  useHFBProjectAllocations,
  useHFBActions,
  useHFBData,
  useHFBRoleTargets,
  useHFBRoleTargetActions,
  useHFBModelDemand
} from '../../hooks/useHFBData';
import { supabase } from '../../supabaseClient';
import { getPersonalityHex, getContrastColor, getUserInitials as getInitialsHelper, getPersonalityName } from '../../utils/personalityHelpers';


// ============================================================================
// DEBUG FLAG - set to false for production
// ============================================================================
var DEBUG_HFB = false;
var DEBUG_SUPERVISOR = true; // Temp debug flag for supervisor persistence issue

function debugLog() {
  if (DEBUG_HFB) {
    console.log.apply(console, ['[HFB]'].concat(Array.prototype.slice.call(arguments)));
  }
}

// ============================================================================
// SCROLLBAR HIDE STYLES - Injected once
// ============================================================================
var scrollbarStylesInjected = false;
function injectScrollbarHideStyles() {
  if (scrollbarStylesInjected) return;
  if (typeof document === 'undefined') return;
  var style = document.createElement('style');
  style.textContent = '.hfb-wheel-scroll::-webkit-scrollbar { display: none; }';
  document.head.appendChild(style);
  scrollbarStylesInjected = true;
}

// ============================================================================
// DRAGGING STYLES - Injected once to prevent magnifying glass / text selection
// ============================================================================
var draggingStylesInjected = false;
function injectDraggingStyles() {
  if (draggingStylesInjected) return;
  if (typeof document === 'undefined') return;
  var style = document.createElement('style');
  style.textContent = 
    'body.hfb-dragging, body.hfb-dragging * {' +
    '  user-select: none !important;' +
    '  -webkit-user-select: none !important;' +
    '  -webkit-user-drag: none !important;' +
    '  cursor: grabbing !important;' +
    '}';
  document.head.appendChild(style);
  draggingStylesInjected = true;
}

// ============================================================================
// TOAST NOTIFICATION SYSTEM
// ============================================================================
function ToastContainer(props) {
  var toasts = props.toasts || [];
  var onDismiss = props.onDismiss;
  if (toasts.length === 0) return null;

  return React.createElement('div', {
    className: 'fixed bottom-4 right-4 space-y-2 max-w-sm',
    style: { pointerEvents: 'none', zIndex: 99999 }
  },
    toasts.map(function(toast) {
      var bgClass = toast.type === 'success' ? 'bg-green-600' :
                    toast.type === 'error' ? 'bg-red-600' :
                    toast.type === 'warning' ? 'bg-amber-500' : 'bg-slate-700';
      var Icon = toast.type === 'success' ? Check :
                 toast.type === 'error' ? AlertCircle :
                 toast.type === 'warning' ? AlertTriangle : AlertCircle;

      return React.createElement('div', {
        key: toast.id,
        className: bgClass + ' text-white px-4 py-3 rounded-lg shadow-lg flex items-start gap-3',
        style: { pointerEvents: 'auto' }
      },
        React.createElement(Icon, { className: 'w-5 h-5 flex-shrink-0 mt-0.5' }),
        React.createElement('div', { className: 'flex-1 text-sm' }, toast.message),
        React.createElement('button', {
          onClick: function() { onDismiss(toast.id); },
          className: 'text-white/70 hover:text-white ml-2'
        }, React.createElement(X, { className: 'w-4 h-4' }))
      );
    })
  );
}

function useToasts() {
  var toastsState = useState([]);
  var toasts = toastsState[0];
  var setToasts = toastsState[1];

  var addToast = useCallback(function(message, type) {
    var id = Date.now() + Math.random();
    setToasts(function(prev) {
      return prev.concat([{ id: id, message: message, type: type || 'info' }]);
    });
    setTimeout(function() {
      setToasts(function(prev) {
        return prev.filter(function(t) { return t.id !== id; });
      });
    }, 4000);
    return id;
  }, []);

  var dismissToast = useCallback(function(id) {
    setToasts(function(prev) {
      return prev.filter(function(t) { return t.id !== id; });
    });
  }, []);

  return { toasts: toasts, addToast: addToast, dismissToast: dismissToast };
}

// ============================================================================
// BRIO ROLE COLORS - Reads CSS tokens from brio-styles.css :root
// ============================================================================
// Hardcoded role color map (matches --brio-role-* tokens in brio-styles.css)
// Avoids module-load timing issues with getComputedStyle resolving to empty strings
var brioRoleColors = {
  'Director': '#a97e53',
  'Partner': '#a97e53',
  'Associate': '#49685a',
  'Senior Architect': '#689581',
  'Project Architect': '#7f7322',
  'Medior Architect': '#b2a655',
  'Junior Architect': '#d9d3aa',
  'Intern': '#f0b596',
  'Support': '#d3b1ab'
};

var FLOATING_ROLES = ['Director', 'Partner', 'Support'];

// Role seniority order: most senior first (lower index = higher rank)
var ROLE_SENIORITY_ORDER = [
  'Director', 'Partner', 'Associate', 'Senior Architect',
  'Project Architect', 'Medior Architect', 'Junior Architect',
  'Intern', 'Support'
];

function compareByRoleSeniority(a, b) {
  var aIdx = ROLE_SENIORITY_ORDER.indexOf(a.jobRole || a.role || '');
  var bIdx = ROLE_SENIORITY_ORDER.indexOf(b.jobRole || b.role || '');
  // Unknown roles go to the end
  if (aIdx === -1) aIdx = 999;
  if (bIdx === -1) bIdx = 999;
  // If same seniority, sort alphabetically by name
  if (aIdx === bIdx) return (a.name || '').localeCompare(b.name || '');
  return aIdx - bIdx;
}

// ============================================================================
// ICON SIZES
// ============================================================================
var USER_AVATAR_SIZE = 60;
var PROJECT_ICON_SIZE = 40;
var PLUS_N_SIZE = 40;

// ============================================================================
// WEEK UTILITIES
// ============================================================================
function getISOWeekData(date) {
  var d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  var dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  var weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNum };
}

function getCurrentISOWeek() {
  var data = getISOWeekData(new Date());
  return data.year + '-W' + String(data.week).padStart(2, '0');
}

function getMondayOfISOWeek(year, week) {
  var jan4 = new Date(Date.UTC(year, 0, 4));
  var dayOfWeek = jan4.getUTCDay() || 7;
  var week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  var targetMonday = new Date(week1Monday);
  targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return targetMonday;
}

function getAdjacentWeek(weekId, delta) {
  var parts = weekId.split('-W');
  var year = parseInt(parts[0], 10);
  var week = parseInt(parts[1], 10);
  var monday = getMondayOfISOWeek(year, week);
  monday.setUTCDate(monday.getUTCDate() + delta * 7);
  var newWeekData = getISOWeekData(monday);
  return newWeekData.year + '-W' + String(newWeekData.week).padStart(2, '0');
}

function isWeekInPast(weekId) {
  return weekId < getCurrentISOWeek();
}

function formatWeekDisplay(weekId) {
  if (!weekId) return '';
  var parts = weekId.split('-W');
  var year = parseInt(parts[0], 10);
  var weekNum = parseInt(parts[1], 10);
  var monday = getMondayOfISOWeek(year, weekNum);
  var sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var startMonth = monthNames[monday.getUTCMonth()];
  var endMonth = monthNames[sunday.getUTCMonth()];
  if (startMonth === endMonth) {
    return 'W' + weekNum + ': ' + startMonth + ' ' + monday.getUTCDate() + '-' + sunday.getUTCDate();
  }
  return 'W' + weekNum + ': ' + startMonth + ' ' + monday.getUTCDate() + ' - ' + endMonth + ' ' + sunday.getUTCDate();
}

// ============================================================================
// COMPACT USER AVATAR - 60px
// ============================================================================
function CompactUserAvatar(props) {
  var user = props.user;
  var teamColor = props.teamColor;
  var size = props.size || USER_AVATAR_SIZE;
  var isSupervisor = props.isSupervisor || false;
  var onClick = props.onClick;
  var isActive = props.isActive;
  var avatarRef = props.avatarRef;
  var totalAllocation = props.totalAllocation; // for status dot
  var avatarMode = props.avatarMode || 'role'; // 'role' | 'photo' | 'personality'
  var profileData = props.profileData; // { personalityTestResult, profilePhotoRound }

  // Hover state for name tooltip
  var hoverState = useState(false);
  var isHovered = hoverState[0];
  var setIsHovered = hoverState[1];

  var roleColor = brioRoleColors[user.jobRole] || '#D4C4A0';
  var strokeColor = teamColor || '#d1d5db';

  var initials = getInitialsHelper(user.name);

  // Determine fill color based on avatar mode
  var fillColor = roleColor;
  var initialsColor = '#ffffff';
  if (avatarMode === 'personality' && profileData && profileData.personalityTestResult) {
    fillColor = getPersonalityHex(profileData.personalityTestResult);
    initialsColor = getContrastColor(fillColor);
  }

  // Check if photo mode can be used
  var hasPhoto = avatarMode === 'photo' && profileData && profileData.profilePhotoRound;

  // Allocation status dot color
  var dotColor = '#9CA3AF'; // grey (under 100%)
  if (totalAllocation !== undefined && totalAllocation !== null) {
    if (totalAllocation === 100) {
      dotColor = '#22C55E'; // green (exact 100%)
    } else if (totalAllocation > 100) {
      dotColor = '#EF4444'; // red (over 100%)
    }
  }
  var dotSize = Math.max(8, size * 0.15); // ~8-10px for 60px avatar

  return React.createElement('div', {
    ref: avatarRef,
    className: 'relative flex-shrink-0 cursor-pointer transition-transform hover:scale-105',
    style: { width: size, height: size },
    onClick: onClick,
    onMouseEnter: function() { setIsHovered(true); },
    onMouseLeave: function() { setIsHovered(false); }
  },
    // Hover name tooltip (above avatar)
    isHovered && React.createElement('div', {
      className: 'absolute left-1/2 px-2 py-1 bg-white border border-slate-200 rounded shadow-sm whitespace-nowrap pointer-events-none',
      style: {
        transform: 'translateX(-50%)',
        bottom: size + 6,
        fontSize: '11px',
        color: '#374151',
        zIndex: 50,
        textAlign: 'center',
        lineHeight: '1.3'
      }
    },
      user.name,
      avatarMode === 'personality' && profileData && profileData.personalityTestResult
        ? React.createElement('div', {
            style: { fontSize: '10px', color: '#6b7280', fontStyle: 'italic' }
          }, getPersonalityName(profileData.personalityTestResult))
        : null
    ),
    // Avatar circle — photo or initials
    hasPhoto
      ? React.createElement('img', {
          src: profileData.profilePhotoRound,
          alt: user.name,
          className: 'w-full h-full rounded-full object-cover',
          style: {
            border: '3px solid ' + strokeColor,
            boxShadow: isActive ? '0 0 0 3px #3b82f6' : 'none'
          }
        })
      : React.createElement('div', {
          className: 'w-full h-full rounded-full flex items-center justify-center font-semibold',
          style: {
            backgroundColor: fillColor,
            color: initialsColor,
            border: '3px solid ' + strokeColor,
            fontSize: size * 0.35 + 'px',
            boxShadow: isActive ? '0 0 0 3px #3b82f6' : 'none'
          }
        }, initials),
    // Supervisor indicator (top-right) - minimal gold 's'
    isSupervisor && React.createElement('span', {
      className: 'absolute font-semibold',
      style: {
        top: -4,
        right: 0,
        fontSize: size * 0.22 + 'px',
        color: '#9f902a',
        lineHeight: 1
      }
    }, 's'),
    // Allocation status dot (bottom-left)
    totalAllocation !== undefined && React.createElement('div', {
      className: 'absolute rounded-full border-2 border-white',
      style: {
        width: dotSize,
        height: dotSize,
        backgroundColor: dotColor,
        bottom: -2,
        left: -2
      },
      title: 'Total allocation: ' + (totalAllocation || 0) + '%'
    })
  );
}

// ============================================================================
// PROJECT ICON - 40px with arc percentage
// ============================================================================
function ProjectIcon(props) {
  var project = props.project;
  var percentage = props.percentage;
  var size = props.size || PROJECT_ICON_SIZE;
  var onRemove = props.onRemove;
  var showRemove = props.showRemove;
  var onEdit = props.onEdit;

  var code = project ? (project.projectCode || project.name || '??').substring(0, 2).toUpperCase() : '??';
  var color = project ? (project.color || '#94a3b8') : '#94a3b8';

  var strokeWidth = 3;
  var radius = (size / 2) - strokeWidth;
  var circumference = 2 * Math.PI * radius;
  var arcLength = (percentage / 100) * circumference;
  var arcOffset = circumference - arcLength;

  return React.createElement('div', {
    className: 'relative group flex-shrink-0' + (onEdit ? ' cursor-pointer' : ''),
    style: { width: size, height: size },
    title: (project ? project.name : 'Unknown') + ' — ' + percentage + '%',
    onClick: onEdit ? function(e) { e.stopPropagation(); onEdit(); } : undefined
  },
    React.createElement('svg', {
      width: size,
      height: size,
      className: 'absolute inset-0'
    },
      React.createElement('circle', {
        cx: size / 2,
        cy: size / 2,
        r: radius,
        fill: 'none',
        stroke: '#e2e8f0',
        strokeWidth: strokeWidth
      }),
      React.createElement('circle', {
        cx: size / 2,
        cy: size / 2,
        r: radius,
        fill: 'none',
        stroke: color,
        strokeWidth: strokeWidth,
        strokeDasharray: circumference,
        strokeDashoffset: arcOffset,
        strokeLinecap: 'round',
        transform: 'rotate(-90 ' + (size / 2) + ' ' + (size / 2) + ')'
      })
    ),
    React.createElement('div', {
      className: 'absolute rounded-full flex items-center justify-center text-white font-bold',
      style: { 
        top: strokeWidth + 1,
        left: strokeWidth + 1,
        right: strokeWidth + 1,
        bottom: strokeWidth + 1,
        backgroundColor: color, 
        fontSize: size * 0.3 + 'px'
      }
    }, code),
    showRemove && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); onRemove && onRemove(); },
      className: 'absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex shadow-sm',
      style: { fontSize: '12px', lineHeight: 1 }
    }, '×')
  );
}

// ============================================================================
// WHEEL PICKER - Matching DonutTimeTracker_Minimal.jsx styling
// Scroll/wheel feel with gradient mask, snap-to-center behavior
// ============================================================================
function WheelPicker(props) {
  var items = props.items || [];
  var selectedValue = props.selectedValue;
  var onSelect = props.onSelect;
  var renderItem = props.renderItem;
  var itemHeight = props.itemHeight || 32;
  var visibleItems = props.visibleItems || 3;
  var disabled = props.disabled;
  
  var wheelRef = useRef(null);
  var height = itemHeight * visibleItems;
  
  // Scroll to selected item on mount/change
  useEffect(function() {
    if (wheelRef.current && selectedValue !== undefined && selectedValue !== '') {
      var index = items.findIndex(function(item) { 
        return (typeof item === 'object' ? item.id : item) === selectedValue; 
      });
      if (index >= 0) {
        wheelRef.current.scrollTop = index * itemHeight;
      }
    }
  }, [selectedValue, items, itemHeight]);
  
  function handleScroll(e) {
    if (disabled) return;
    var scrollTop = e.target.scrollTop;
    var index = Math.round(scrollTop / itemHeight);
    if (items[index]) {
      var item = items[index];
      var isDisabled = typeof item === 'object' && item.disabled;
      if (!isDisabled) {
        var value = typeof item === 'object' ? item.id : item;
        onSelect && onSelect(value);
      }
    }
  }
  
  return React.createElement('div', {
    className: 'relative bg-transparent rounded-lg overflow-hidden',
    style: {
      height: height,
      WebkitMaskImage: 'linear-gradient(to bottom, transparent, white 30%, white 70%, transparent)',
      maskImage: 'linear-gradient(to bottom, transparent, white 30%, white 70%, transparent)',
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
      opacity: disabled ? 0.5 : 1
    }
  },
    React.createElement('div', {
      ref: wheelRef,
      className: 'absolute inset-0 overflow-y-scroll scrollbar-hide',
      style: {
        scrollSnapType: 'y mandatory',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'transparent'
      },
      onScroll: handleScroll
    },
      React.createElement('div', { style: { height: itemHeight * 0.5 } }),
      items.map(function(item, idx) {
        var value = typeof item === 'object' ? item.id : item;
        var isSelected = value === selectedValue;
        var isDisabled = typeof item === 'object' && item.disabled;
        return React.createElement('div', {
          key: value !== undefined ? value : idx,
          className: 'flex items-center justify-center font-bold cursor-pointer hover:bg-transparent transition-all',
          style: {
            height: itemHeight,
            scrollSnapAlign: 'center',
            opacity: isDisabled ? 0.2 : (isSelected ? 1 : 0.3),
            transform: isSelected ? 'scale(1.1)' : 'scale(0.9)',
            color: '#1e293b',
            fontSize: '11px',
            pointerEvents: isDisabled ? 'none' : 'auto'
          },
          onClick: function(e) {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled && !disabled) {
              onSelect && onSelect(value);
            }
          }
        }, renderItem ? renderItem(item, isSelected) : (typeof item === 'object' ? item.name : String(item)));
      }),
      React.createElement('div', { style: { height: itemHeight * 0.5 } })
    )
  );
}

// ============================================================================
// INLINE ALLOCATION POPOVER - Symmetric 3-column layout
// Left rail (% text) | Center rail (spacer for avatar) | Right rail (wheels)
// Avatar stays in place, popover expands around it with perfect center alignment
// ============================================================================

// Layout constants
var POPOVER_SIDE_WIDTH = 180; // Width of left and right rails

function InlineAllocationPopover(props) {
  var user = props.user;
  var allocations = props.allocations || [];
  var projects = props.projects || [];
  var totalAllocation = props.totalAllocation || 0;
  var onClose = props.onClose;
  var onAddAllocation = props.onAddAllocation;
  var onUpdateAllocation = props.onUpdateAllocation;
  var onRemoveAllocation = props.onRemoveAllocation;
  var isPastWeek = props.isPastWeek;
  var avatarSize = props.avatarSize || USER_AVATAR_SIZE;
  var editingAllocation = props.editingAllocation || null; // { id, projectId, percentage } for edit mode

  var popoverRef = useRef(null);

  var selectedProjectState = useState(editingAllocation ? editingAllocation.projectId : '');
  var selectedProject = selectedProjectState[0];
  var setSelectedProject = selectedProjectState[1];

  var isEditMode = !!editingAllocation;

  // For edit mode: remaining = other allocations + current (so user can re-pick within range)
  var editingCurrentPct = isEditMode ? editingAllocation.percentage : 0;
  var remaining = 100 - totalAllocation + editingCurrentPct;

  // Total popover width: left rail + center (avatar) + right rail
  var totalWidth = POPOVER_SIDE_WIDTH + avatarSize + POPOVER_SIDE_WIDTH;

  // Inject scrollbar hide styles on mount
  useEffect(function() {
    injectScrollbarHideStyles();
  }, []);

  // Build percentage options (10% increments, capped to remaining availability)
  var effectiveRemaining = Math.max(0, remaining);
  var percentageOptions = [];
  for (var i = 0; i <= 100; i += 10) {
    if (i > effectiveRemaining) break; // Stop at remaining — no options beyond availability
    percentageOptions.push({
      id: i,
      name: i + '%'
    });
  }
  // Include the exact remaining value if it's not a multiple of 10 and > 0
  if (effectiveRemaining > 0 && effectiveRemaining % 10 !== 0 && percentageOptions[percentageOptions.length - 1].id !== effectiveRemaining) {
    percentageOptions.push({ id: effectiveRemaining, name: effectiveRemaining + '%' });
  }

  // Get projects not already allocated (plannable projects)
  var allocatedProjectIds = allocations.map(function(a) { return String(a.projectId); });
  var availableProjects = isEditMode
    ? projects.filter(function(p) {
        // In edit mode, show only the project being edited
        return String(p.id) === String(editingAllocation.projectId);
      })
    : projects.filter(function(p) {
        return allocatedProjectIds.indexOf(String(p.id)) === -1;
      }).sort(function(a, b) {
        return (a.name || '').localeCompare(b.name || '');
      });

  // Set initial selected project
  useEffect(function() {
    if (editingAllocation) {
      setSelectedProject(editingAllocation.projectId);
    } else if (availableProjects.length > 0 && !selectedProject) {
      setSelectedProject(availableProjects[0].id);
    }
  }, [availableProjects, selectedProject, editingAllocation]);
  
  // Handle Escape key
  useEffect(function() {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return function() { document.removeEventListener('keydown', handleKeyDown); };
  }, [onClose]);
  
  // Handle click outside (closes without saving)
  useEffect(function() {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        if (!e.target.closest('[data-popover-trigger]')) {
          onClose && onClose();
        }
      }
    }
    var timer = setTimeout(function() {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return function() { 
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside); 
    };
  }, [onClose]);
  
  // Handle project selection (does not save yet)
  function handleSelectProject(projectId) {
    setSelectedProject(projectId);
  }
  
  // Handle percentage pick - this confirms and saves
  function handlePickPercentage(pct) {
    if (!selectedProject || pct <= 0) return;
    if (pct > remaining) return;
    if (isPastWeek) return;

    if (isEditMode) {
      // Edit mode: always update existing allocation
      onUpdateAllocation && onUpdateAllocation(editingAllocation.id, { percentage: pct });
    } else {
      var existing = allocations.find(function(a) {
        return String(a.projectId) === String(selectedProject);
      });

      if (existing) {
        onUpdateAllocation && onUpdateAllocation(existing.id, { percentage: pct });
      } else {
        onAddAllocation && onAddAllocation({
          projectId: selectedProject,
          percentage: pct
        });
      }
    }

    onClose && onClose();
  }
  
  // Scrollbar-hiding styles
  var scrollHideStyle = {
    scrollSnapType: 'y mandatory',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none'
  };
  
  // If past week or fully allocated (and not editing) or no projects, show minimal read-only state
  if (isPastWeek || (!isEditMode && (remaining <= 0 || availableProjects.length === 0))) {
    var statusText = isPastWeek ? 'Locked' : 
                     remaining <= 0 ? '100%' : 
                     'N/A';
    
    // Symmetric layout even for read-only: left rail | center spacer | (no right rail content)
    return React.createElement('div', {
      ref: popoverRef,
      className: 'absolute flex items-center pointer-events-none',
      style: { 
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: totalWidth,
        height: avatarSize,
        zIndex: 99999,
        background: 'transparent',
        border: 'none',
        boxShadow: 'none'
      }
    },
      // Left rail: percentage in white circle
      React.createElement('div', { 
        className: 'flex items-center justify-end pointer-events-auto',
        style: { width: POPOVER_SIDE_WIDTH, height: avatarSize, paddingRight: 8 }
      },
        React.createElement('div', {
          className: 'rounded-full bg-white flex items-center justify-center',
          style: { width: avatarSize, height: avatarSize }
        },
          React.createElement('span', { 
            className: 'font-bold text-black whitespace-nowrap',
            style: { fontSize: '10px' }
          }, statusText)
        )
      ),
      // Center rail: spacer (avatar visible behind)
      React.createElement('div', {
        style: { width: avatarSize, height: avatarSize, flexShrink: 0 }
      }),
      // Right rail: empty for read-only
      React.createElement('div', { 
        style: { width: POPOVER_SIDE_WIDTH, height: avatarSize }
      })
    );
  }
  
  // Main popover - symmetric 3-column layout
  return React.createElement('div', {
    ref: popoverRef,
    className: 'absolute flex items-center pointer-events-none',
    style: { 
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: totalWidth,
      height: avatarSize,
      zIndex: 99999,
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      outline: 'none'
    }
  },
    // LEFT RAIL: percentage in white circle (black text, half size)
    React.createElement('div', { 
      className: 'flex items-center justify-end pointer-events-auto',
      style: { width: POPOVER_SIDE_WIDTH, height: avatarSize, paddingRight: 8 }
    },
      React.createElement('div', {
        className: 'rounded-full bg-white flex items-center justify-center',
        style: { width: avatarSize, height: avatarSize }
      },
        React.createElement('span', { 
          className: 'font-bold text-black whitespace-nowrap',
          style: { fontSize: '10px' }
        }, totalAllocation + '%')
      )
    ),
    
    // CENTER RAIL: spacer for the avatar (avatar stays in place behind this)
    React.createElement('div', {
      style: { width: avatarSize, height: avatarSize, flexShrink: 0 }
    }),
    
    // RIGHT RAIL: Two picker wheels
    React.createElement('div', { 
      className: 'flex items-center gap-2 pointer-events-auto',
      style: { width: POPOVER_SIDE_WIDTH, height: avatarSize, paddingLeft: 8 }
    },
      // Project picker wheel - white circle, NO border, NO shadow
      React.createElement('div', {
        className: 'rounded-full bg-white flex items-center justify-center overflow-hidden',
        style: { 
          width: avatarSize, 
          height: avatarSize,
          border: 'none',
          boxShadow: 'none',
          outline: 'none'
        },
        title: 'Select project'
      },
        React.createElement('div', {
          className: 'hfb-wheel-scroll w-full h-full overflow-y-auto flex flex-col items-center justify-start py-1',
          style: scrollHideStyle
        },
          availableProjects.map(function(project) {
            var isSelected = String(selectedProject) === String(project.id);
            return React.createElement('div', {
              key: project.id,
              className: 'w-full px-1 py-1 text-center cursor-pointer transition-all flex-shrink-0',
              style: { 
                scrollSnapAlign: 'center',
                color: '#1e293b',
                fontWeight: isSelected ? '700' : '400',
                fontSize: isSelected ? '11px' : '10px',
                opacity: isSelected ? 1 : 0.6,
                background: 'transparent'
              },
              onClick: function() { handleSelectProject(project.id); }
            }, 
              isSelected ? '● ' : '',
              project.projectCode || project.name.substring(0, 5)
            );
          })
        )
      ),
      
      // Percentage picker wheel - white circle, NO border, clicking saves
      React.createElement('div', {
        className: 'rounded-full bg-white flex items-center justify-center overflow-hidden',
        style: { 
          width: avatarSize, 
          height: avatarSize,
          border: 'none',
          boxShadow: 'none',
          outline: 'none'
        },
        title: 'Click percentage to save'
      },
        React.createElement('div', {
          className: 'hfb-wheel-scroll w-full h-full overflow-y-auto flex flex-col items-center justify-start py-1',
          style: scrollHideStyle
        },
          percentageOptions.slice(1).map(function(pct) {
            return React.createElement('div', {
              key: pct.id,
              className: 'w-full px-1 py-1 text-center cursor-pointer transition-all flex-shrink-0',
              style: {
                scrollSnapAlign: 'center',
                color: '#1e293b',
                fontWeight: '600',
                fontSize: '11px',
                cursor: 'pointer',
                background: 'transparent'
              },
              onClick: function() {
                handlePickPercentage(pct.id);
              },
              title: 'Click to save ' + pct.name
            }, pct.name);
          })
        )
      )
    )
  );
}

// ============================================================================
// ACTIVE PROJECT ROW COMPONENT
// Displays a single project with FTE, role breakdown, and target inputs
// REDESIGN: Minimal, narrower layout with aligned A/T (Allocated/Target) sections
// ============================================================================
function ActiveProjectRow(props) {
  var project = props.project;
  var allocatedFte = props.allocatedFte;
  var roleBreakdown = props.roleBreakdown; // Array of { initial, fte }
  var targets = props.targets || {}; // { [roleInitial]: { targetFte, ... } }
  var onTargetChange = props.onTargetChange;
  var canEditTargets = props.canEditTargets;
  var isPastWeek = props.isPastWeek;
  var showModelDemand = props.showModelDemand || false;
  var modelDemand = props.modelDemand || {}; // { roleInitial: fte } (studio-level)
  
  var expandedState = useState(false);
  var isExpanded = expandedState[0];
  var setIsExpanded = expandedState[1];
  
  // Local state for target inputs (optimistic UI)
  var localTargetsState = useState({});
  var localTargets = localTargetsState[0];
  var setLocalTargets = localTargetsState[1];
  
  // Sync local targets from props
  useEffect(function() {
    var initial = {};
    Object.keys(targets).forEach(function(key) {
      initial[key] = targets[key].targetFte;
    });
    roleBreakdown.forEach(function(rb) {
      if (initial[rb.initial] === undefined) {
        initial[rb.initial] = '';
      }
    });
    setLocalTargets(initial);
  }, [targets, roleBreakdown]);
  
  // Calculate target total
  var targetTotal = useMemo(function() {
    var sum = 0;
    Object.values(localTargets).forEach(function(val) {
      var num = parseFloat(val);
      if (!isNaN(num)) sum += num;
    });
    return sum;
  }, [localTargets]);
  
  // Handle target input change with debounce
  var debounceRef = useRef({});
  function handleTargetInputChange(roleInitial, value) {
    // Update local state immediately (optimistic)
    setLocalTargets(function(prev) {
      var updated = { ...prev };
      updated[roleInitial] = value;
      return updated;
    });
    
    // Debounce the save
    if (debounceRef.current[roleInitial]) {
      clearTimeout(debounceRef.current[roleInitial]);
    }
    
    var numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) {
      // Don't save empty/invalid values immediately
      return;
    }
    
    debounceRef.current[roleInitial] = setTimeout(function() {
      onTargetChange && onTargetChange(project.id, roleInitial, numValue);
    }, 500);
  }
  
  // Cleanup debounce timers
  useEffect(function() {
    return function() {
      Object.values(debounceRef.current).forEach(clearTimeout);
    };
  }, []);
  
  // Format FTE for display (1 decimal)
  function formatFte(value) {
    if (value === 0) return '0.0';
    var rounded = Math.round(value * 10) / 10;
    return rounded.toFixed(1);
  }
  
  var projectColor = project.color || '#94a3b8';
  // Show only project code (or first 4 chars of name as fallback)
  var projectCode = (project.projectCode || (project.name || '??').substring(0, 4)).toUpperCase();
  
  return React.createElement('div', {
    className: 'border-b border-slate-100 last:border-b-0'
  },
    // Project header row - collapsed view: code + number only
    React.createElement('div', {
      className: 'flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 transition-colors',
      onClick: function() { setIsExpanded(!isExpanded); }
    },
      // Project color dot
      React.createElement('div', {
        className: 'w-2 h-2 rounded-full flex-shrink-0',
        style: { backgroundColor: projectColor }
      }),
      // Project code only
      React.createElement('span', { 
        className: 'text-xs font-medium text-slate-700 flex-1',
        title: project.name
      }, projectCode),
      // Allocated FTE value only (no "FTE" text)
      React.createElement('span', { 
        className: 'text-xs font-semibold text-slate-600 tabular-nums'
      }, formatFte(allocatedFte)),
      // Expand/collapse icon
      React.createElement('div', { className: 'text-slate-400' },
        isExpanded 
          ? React.createElement(ChevronUp, { className: 'w-3 h-3' })
          : React.createElement(ChevronDown, { className: 'w-3 h-3' })
      )
    ),
    
    // Expanded section - NORMALIZED A/T alignment with identical token layout
    isExpanded && React.createElement('div', {
      className: 'px-2 pb-2 pt-1 bg-slate-50/50'
    },
      // Outer table: fixed label column + value column
      React.createElement('table', {
        style: { width: '100%', borderCollapse: 'collapse' }
      },
        React.createElement('tbody', null,
          // Row A
          React.createElement('tr', null,
            React.createElement('td', {
              style: { 
                width: '16px', 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px'
              }
            },
              React.createElement('span', { 
                style: { fontSize: '12px', lineHeight: '18px', color: '#64748b' }
              }, 'A')
            ),
            React.createElement('td', {
              style: { 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px',
                paddingBottom: '2px'
              }
            },
              // A token line - inline-flex baseline aligned with fixed-width numeric boxes
              React.createElement('span', {
                style: { 
                  display: 'inline-flex', 
                  alignItems: 'baseline', 
                  gap: '0',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  color: '#475569'
                }
              },
                roleBreakdown.length > 0 
                  ? roleBreakdown.map(function(rb, idx) {
                      return React.createElement('span', {
                        key: rb.initial,
                        style: { display: 'inline-flex', alignItems: 'baseline', gap: '0' }
                      }, 
                        idx > 0 ? React.createElement('span', { 
                          style: { color: '#cbd5e1', margin: '0 4px' }
                        }, '|') : null,
                        // Fixed-width numeric box for A value
                        React.createElement('span', {
                          style: {
                            display: 'inline-block',
                            width: '3ch',
                            minWidth: '3ch',
                            maxWidth: '3ch',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum"'
                          }
                        }, formatFte(rb.fte)),
                        // Role letter immediately after
                        React.createElement('span', null, rb.initial)
                      );
                    })
                  : React.createElement('span', { style: { color: '#94a3b8' } }, '—')
              ),
              // Total on right
              React.createElement('span', { 
                style: { 
                  float: 'right',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  color: '#334155',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"'
                }
              }, formatFte(allocatedFte))
            )
          ),
          
          // Row T
          React.createElement('tr', null,
            React.createElement('td', {
              style: { 
                width: '16px', 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px'
              }
            },
              React.createElement('span', { 
                style: { fontSize: '12px', lineHeight: '18px', color: '#64748b' }
              }, 'T')
            ),
            React.createElement('td', {
              style: { 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px',
                paddingBottom: '2px'
              }
            },
              // T token line - identical layout to A with fixed-width numeric boxes
              React.createElement('span', {
                style: { 
                  display: 'inline-flex', 
                  alignItems: 'baseline', 
                  gap: '0',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  color: '#475569'
                }
              },
                roleBreakdown.length > 0 
                  ? roleBreakdown.map(function(rb, idx) {
                      var currentValue = localTargets[rb.initial];
                      if (currentValue === undefined) currentValue = '';
                      
                      return React.createElement('span', {
                        key: rb.initial,
                        style: { display: 'inline-flex', alignItems: 'baseline', gap: '0' }
                      }, 
                        idx > 0 ? React.createElement('span', { 
                          style: { color: '#cbd5e1', margin: '0 4px' }
                        }, '|') : null,
                        // Fixed-width input box for T value - exactly matches A numeric box
                        React.createElement('input', {
                          type: 'number',
                          step: '0.1',
                          min: '0',
                          value: currentValue,
                          onChange: function(e) { 
                            if (canEditTargets && !isPastWeek) {
                              handleTargetInputChange(rb.initial, e.target.value);
                            }
                          },
                          disabled: !canEditTargets || isPastWeek,
                          placeholder: '—',
                          style: { 
                            width: '3ch',
                            minWidth: '3ch',
                            maxWidth: '3ch',
                            padding: '0',
                            margin: '0',
                            border: '0',
                            background: 'transparent',
                            boxShadow: 'none',
                            outline: 'none',
                            fontSize: '12px',
                            lineHeight: '18px',
                            fontWeight: '600',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum"',
                            textAlign: 'right',
                            color: canEditTargets && !isPastWeek ? '#475569' : '#94a3b8'
                          }
                        }),
                        // Role letter immediately after
                        React.createElement('span', null, rb.initial)
                      );
                    })
                  : React.createElement('span', { style: { color: '#94a3b8' } }, '—')
              ),
              // Total on right
              React.createElement('span', { 
                style: { 
                  float: 'right',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"',
                  color: targetTotal > 0 ? '#2563eb' : '#94a3b8'
                }
              }, targetTotal > 0 ? formatFte(targetTotal) : '—')
            )
          ),
          
          // Row M (Model demand) - only when overlay enabled and has data
          showModelDemand && Object.keys(modelDemand).length > 0 && React.createElement('tr', null,
            React.createElement('td', {
              style: { 
                width: '16px', 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px'
              }
            },
              React.createElement('span', { 
                style: { fontSize: '12px', lineHeight: '18px', color: '#d97706' }
              }, 'M')
            ),
            React.createElement('td', {
              style: { 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px',
                paddingBottom: '2px'
              }
            },
              React.createElement('span', {
                style: { 
                  display: 'inline-flex', 
                  alignItems: 'baseline', 
                  gap: '0',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  color: '#d97706'
                }
              },
                roleBreakdown.length > 0 
                  ? roleBreakdown.map(function(rb, idx) {
                      var mVal = modelDemand[rb.initial] || 0;
                      return React.createElement('span', {
                        key: rb.initial,
                        style: { display: 'inline-flex', alignItems: 'baseline', gap: '0' }
                      }, 
                        idx > 0 ? React.createElement('span', { 
                          style: { color: '#cbd5e1', margin: '0 4px' }
                        }, '|') : null,
                        React.createElement('span', {
                          style: {
                            display: 'inline-block',
                            width: '3ch',
                            minWidth: '3ch',
                            maxWidth: '3ch',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum"'
                          }
                        }, formatFte(mVal)),
                        React.createElement('span', null, rb.initial)
                      );
                    })
                  : React.createElement('span', { style: { color: '#94a3b8' } }, '—')
              ),
              // Model total on right
              React.createElement('span', { 
                style: { 
                  float: 'right',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  color: '#d97706',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"'
                }
              }, (function() {
                var mTotal = 0;
                roleBreakdown.forEach(function(rb) { mTotal += (modelDemand[rb.initial] || 0); });
                return mTotal > 0 ? formatFte(mTotal) : '—';
              })())
            )
          ),
          
          // Row Δ (Delta) - only when overlay enabled and has data
          showModelDemand && Object.keys(modelDemand).length > 0 && React.createElement('tr', null,
            React.createElement('td', {
              style: { 
                width: '16px', 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px'
              }
            },
              React.createElement('span', { 
                style: { fontSize: '12px', lineHeight: '18px', color: '#64748b' }
              }, 'Δ')
            ),
            React.createElement('td', {
              style: { 
                verticalAlign: 'top', 
                textAlign: 'left',
                paddingTop: '2px',
                paddingBottom: '2px'
              }
            },
              React.createElement('span', {
                style: { 
                  display: 'inline-flex', 
                  alignItems: 'baseline', 
                  gap: '0',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600'
                }
              },
                roleBreakdown.length > 0 
                  ? roleBreakdown.map(function(rb, idx) {
                      var mVal = modelDemand[rb.initial] || 0;
                      var tVal = parseFloat(localTargets[rb.initial]) || 0;
                      var delta = mVal - tVal;
                      var deltaColor = delta > 0.25 ? '#dc2626' : (delta < -0.25 ? '#94a3b8' : '#475569');
                      return React.createElement('span', {
                        key: rb.initial,
                        style: { display: 'inline-flex', alignItems: 'baseline', gap: '0' }
                      }, 
                        idx > 0 ? React.createElement('span', { 
                          style: { color: '#cbd5e1', margin: '0 4px' }
                        }, '|') : null,
                        React.createElement('span', {
                          style: {
                            display: 'inline-block',
                            width: '3ch',
                            minWidth: '3ch',
                            maxWidth: '3ch',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            fontFeatureSettings: '"tnum"',
                            color: deltaColor
                          }
                        }, (delta > 0 ? '+' : '') + delta.toFixed(1)),
                        React.createElement('span', { style: { color: deltaColor } }, rb.initial),
                        delta > 0.25 ? React.createElement('span', {
                          style: { 
                            fontSize: '9px', 
                            color: '#dc2626', 
                            marginLeft: '1px',
                            fontWeight: '700'
                          }
                        }, '⚠') : null
                      );
                    })
                  : React.createElement('span', { style: { color: '#94a3b8' } }, '—')
              ),
              // Delta total + badge on right
              React.createElement('span', { 
                style: { 
                  float: 'right',
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '600',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"'
                }
              }, (function() {
                var sumShort = 0;
                roleBreakdown.forEach(function(rb) {
                  var d = (modelDemand[rb.initial] || 0) - (parseFloat(localTargets[rb.initial]) || 0);
                  if (d > 0) sumShort += d;
                });
                if (sumShort > 0.25) {
                  return React.createElement('span', { style: { color: '#dc2626' } }, '+' + sumShort.toFixed(1));
                }
                return React.createElement('span', { style: { color: '#16a34a' } }, 'OK');
              })())
            )
          )
        )
      )
    )
  );
}

// ============================================================================
// ACTIVE PROJECTS COLUMN COMPONENT
// Displays all active projects with FTE calculations for the selected week
// ============================================================================
function ActiveProjectsColumn(props) {
  var projects = props.projects || [];
  var users = props.users || [];
  var allocations = props.allocations || [];
  var targets = props.targets || {};
  var onTargetChange = props.onTargetChange;
  var canEditTargets = props.canEditTargets;
  var isPastWeek = props.isPastWeek;
  var maxHeight = props.maxHeight;
  var showModelDemand = props.showModelDemand || false;
  var modelDemand = props.modelDemand || {};       // { roleInitial: fte }
  var modelDemandNoData = props.modelDemandNoData || false;
  var modelDemandError = props.modelDemandError || null;
  var modelDemandRunInfo = props.modelDemandRunInfo || null;
  
  // Filter to active projects only
  var activeProjects = useMemo(function() {
    return projects.filter(function(p) {
      return p.status === 'active';
    }).sort(function(a, b) {
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [projects]);
  
  // Build user role map
  var userRoleMap = useMemo(function() {
    var map = {};
    users.forEach(function(u) {
      map[String(u.id)] = u.jobRole || 'Unknown';
    });
    return map;
  }, [users]);
  
  // Calculate FTE per project and role breakdown
  var projectFteData = useMemo(function() {
    var data = {};
    
    activeProjects.forEach(function(project) {
      var projectId = String(project.id);
      var projectAllocations = allocations.filter(function(a) {
        return String(a.projectId) === projectId;
      });
      
      // Calculate total FTE: sum((percentage / 100) * 44) / 44
      // Which simplifies to sum(percentages) / 100
      var totalPercentage = 0;
      var roleHours = {};
      
      projectAllocations.forEach(function(alloc) {
        var percentage = alloc.percentage || 0;
        totalPercentage += percentage;
        
        // Get user's role
        var userRole = userRoleMap[String(alloc.userId)] || 'Unknown';
        var roleInitial = userRole.charAt(0).toUpperCase();
        
        // Calculate hours for this allocation: (percentage / 100) * 44
        var hours = (percentage / 100) * 44;
        
        if (!roleHours[roleInitial]) {
          roleHours[roleInitial] = 0;
        }
        roleHours[roleInitial] += hours;
      });
      
      // Total FTE = totalHours / 44
      var totalHours = (totalPercentage / 100) * 44;
      var totalFte = totalHours / 44;
      
      // Role breakdown: FTE = roleHours / 44
      var roleBreakdown = Object.keys(roleHours).sort().map(function(initial) {
        return {
          initial: initial,
          fte: roleHours[initial] / 44
        };
      });
      
      data[projectId] = {
        totalFte: totalFte,
        roleBreakdown: roleBreakdown
      };
    });
    
    return data;
  }, [activeProjects, allocations, userRoleMap]);
  
  // Compute studio-level shortage summary when model demand is shown
  var studioShortage = useMemo(function() {
    if (!showModelDemand || Object.keys(modelDemand).length === 0) return 0;
    var sum = 0;
    Object.keys(modelDemand).forEach(function(initial) {
      // Sum target FTE across all projects for this role
      var totalTarget = 0;
      Object.keys(targets).forEach(function(pid) {
        var tgt = targets[pid][initial];
        if (tgt && tgt.targetFte) totalTarget += parseFloat(tgt.targetFte) || 0;
      });
      var delta = (modelDemand[initial] || 0) - totalTarget;
      if (delta > 0) sum += delta;
    });
    return sum;
  }, [showModelDemand, modelDemand, targets]);

  if (activeProjects.length === 0) {
    return React.createElement('div', {
      className: 'flex items-center justify-center text-xs text-slate-400 italic py-4'
    }, 'No projects');
  }
  
  return React.createElement('div', {
    className: 'overflow-y-auto',
    style: { maxHeight: maxHeight || '100%' }
  },
    // Model Demand banner (studio-level) when overlay is on
    showModelDemand && React.createElement('div', {
      className: 'px-2 py-1.5 border-b border-amber-200',
      style: { backgroundColor: '#fffbeb' }
    },
      // Real Supabase error
      // Real Supabase error — only when error object is non-null
      modelDemandError
        ? React.createElement('div', {
            className: 'text-xs text-red-600 italic leading-snug'
          }, 
            React.createElement(AlertTriangle, { 
              className: 'w-3 h-3 inline-block mr-1', 
              style: { verticalAlign: 'text-bottom' } 
            }),
            'Model demand error — check console for details'
          )
      // No runs with demand rows for these months
      : modelDemandNoData 
        ? React.createElement('div', {
            className: 'text-xs text-amber-700 italic leading-snug'
          }, 
            'No model demand available for this period. Run a forecast in Analytics and ensure stage anchors exist.'
          )
      // Data available
      : React.createElement('div', null,
            React.createElement('div', {
              className: 'text-xs font-medium text-amber-800 mb-0.5'
            }, 'Studio model demand'),
            // Show which run is being used
            modelDemandRunInfo && React.createElement('div', {
              className: 'text-xs text-amber-600 mb-0.5',
              style: { fontStyle: 'italic' }
            }, 'Using forecast run: ' + 
              new Date(modelDemandRunInfo.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + 
              ' (' + (modelDemandRunInfo.horizon_months || '?') + 'm)'
            ),
            // Show shortage summary
            studioShortage > 0.25 
              ? React.createElement('div', {
                  className: 'text-xs font-semibold text-red-600'
                }, '⚠ Short: +' + studioShortage.toFixed(2) + ' FTE')
              : React.createElement('div', {
                  className: 'text-xs text-emerald-600'
                }, 'Targets meet demand'),
            // Compact role breakdown
            React.createElement('div', {
              className: 'flex flex-wrap gap-x-1.5 gap-y-0 mt-0.5'
            },
              Object.keys(modelDemand).sort().map(function(initial) {
                return React.createElement('span', {
                  key: initial,
                  className: 'text-xs tabular-nums text-amber-700',
                  style: { fontVariantNumeric: 'tabular-nums' }
                }, (modelDemand[initial] || 0).toFixed(1) + initial);
              })
            )
          )
    ),
    activeProjects.map(function(project) {
      var projectId = String(project.id);
      var fteData = projectFteData[projectId] || { totalFte: 0, roleBreakdown: [] };
      var projectTargets = targets[projectId] || {};
      
      return React.createElement(ActiveProjectRow, {
        key: project.id,
        project: project,
        allocatedFte: fteData.totalFte,
        roleBreakdown: fteData.roleBreakdown,
        targets: projectTargets,
        onTargetChange: onTargetChange,
        canEditTargets: canEditTargets,
        isPastWeek: isPastWeek,
        showModelDemand: showModelDemand,
        modelDemand: modelDemand
      });
    })
  );
}

// ============================================================================
// DRAGGABLE USER COMPONENT
// ============================================================================
function DraggableUser(props) {
  var user = props.user;
  var teamId = props.teamId;
  var disabled = props.disabled;
  var children = props.children;
  
  var dragProps = useDraggable({
    id: 'user:' + String(user.id),
    disabled: disabled,
    data: { type: 'user', user: user, userId: String(user.id), fromTeamId: teamId }
  });

  var style = {};
  if (dragProps.transform) {
    style.transform = 'translate3d(' + dragProps.transform.x + 'px, ' + dragProps.transform.y + 'px, 0)';
    style.zIndex = 1000;
  }
  if (dragProps.isDragging) {
    style.opacity = 0;
  }

  return React.createElement('div', {
    ref: dragProps.setNodeRef,
    style: style,
    draggable: false,
    ...dragProps.attributes,
    ...dragProps.listeners
  }, children);
}

// ============================================================================
// DROPPABLE TEAM COLUMN
// Phase 4.6: Remove blue tint, use neutral highlight only on isOver
// ============================================================================
function DroppableTeamColumn(props) {
  var team = props.team;
  var disabled = props.disabled;
  var isDragging = props.isDragging;
  var isFullScreen = props.isFullScreen;
  var isFocused = props.isFocused;
  var children = props.children;
  var boardHeight = props.boardHeight; // Phase 4.6: Pass board height for full-height droppable

  var dropProps = useDroppable({
    id: 'team:' + String(team.id),
    disabled: disabled,
    data: { type: 'team', team: team, teamId: team.id }
  });

  // Phase 4.6: Remove blue tint - use neutral highlight only on isOver
  var bgColor = 'transparent';
  if (dropProps.isOver) {
    bgColor = '#fafaf5'; // Neutral warm highlight on drag over
  }
  // Remove the isDragging blue tint entirely

  // In focused mode, the single team expands to fill available width
  var minW = isFocused ? '400px' : (isFullScreen ? '160px' : '140px');
  var flex = isFocused ? '1 1 auto' : (isFullScreen ? '1 1 auto' : '0 0 140px');
  var maxW = isFocused ? 'none' : (isFullScreen ? '220px' : '140px');

  return React.createElement('div', {
    ref: dropProps.setNodeRef,
    className: 'flex flex-col transition-colors',
    style: {
      minWidth: minW,
      flex: flex,
      maxWidth: maxW,
      backgroundColor: bgColor,
      // Phase 4.6: Force consistent droppable height
      height: boardHeight ? boardHeight + 'px' : '100%',
      minHeight: '100%'
    }
  }, children);
}

// ============================================================================
// DROPPABLE ZONE (for Unassigned and Floating)
// Phase 4.6: Remove blue tint, use neutral highlight only on isOver
// ============================================================================
function DroppableZone(props) {
  var zoneId = props.zoneId;
  var disabled = props.disabled;
  var isDragging = props.isDragging;
  var children = props.children;
  
  var dropProps = useDroppable({
    id: zoneId,
    disabled: disabled,
    data: { type: 'pool', zoneId: zoneId }
  });

  // Phase 4.6: Remove blue tint - use neutral highlight only on isOver
  var bgColor = 'transparent';
  if (dropProps.isOver) {
    bgColor = '#fafaf5'; // Neutral warm highlight on drag over
  }
  // Remove the isDragging blue tint entirely

  return React.createElement('div', { 
    ref: dropProps.setNodeRef,
    className: 'transition-colors',
    style: {
      backgroundColor: bgColor,
      minHeight: '80px'
    }
  }, children);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function HybridFocusBoard(props) {
  var currentUser = props.currentUser;
  var users = props.users || [];
  var projects = props.projects || [];
  var teams = props.teams || [];
  var onUpdateTeamSupervisors = props.onUpdateTeamSupervisors; // Callback to persist supervisors to Supabase

  var toastHook = useToasts();
  var toasts = toastHook.toasts;
  var addToast = toastHook.addToast;
  var dismissToast = toastHook.dismissToast;

  var weekState = useState(function() { return getCurrentISOWeek(); });
  var currentWeekId = weekState[0];
  var setCurrentWeekId = weekState[1];
  
  var isPastWeek = useMemo(function() { return isWeekInPast(currentWeekId); }, [currentWeekId]);
  var isCurrentWeek = currentWeekId === getCurrentISOWeek();

  var fullScreenState = useState(false);
  var isFullScreen = fullScreenState[0];
  var setIsFullScreen = fullScreenState[1];
  
  var showAllocationsState = useState(true);
  var showAllocations = showAllocationsState[0];
  var setShowAllocations = showAllocationsState[1];
  
  // Active Projects column visibility toggle - default ON
  var showActiveProjectsState = useState(true);
  var showActiveProjects = showActiveProjectsState[0];
  var setShowActiveProjects = showActiveProjectsState[1];

  // Focused team view state - when set, only shows this team
  var focusedTeamState = useState(null);
  var focusedTeamId = focusedTeamState[0];
  var setFocusedTeamId = focusedTeamState[1];

  var poolsCollapsedState = useState(true);
  var poolsCollapsed = poolsCollapsedState[0];
  var setPoolsCollapsed = poolsCollapsedState[1];

  // ============================================================================
  // SUPERVISOR EDIT MODAL STATE
  // ============================================================================
  var supervisorModalState = useState(null); // { teamId, teamName, currentSupervisorIds }
  var supervisorModal = supervisorModalState[0];
  var setSupervisorModal = supervisorModalState[1];
  
  var selectedSupervisorsState = useState([]);
  var selectedSupervisors = selectedSupervisorsState[0];
  var setSelectedSupervisors = selectedSupervisorsState[1];
  
  var savingSupervisorsState = useState(false);
  var savingSupervisors = savingSupervisorsState[0];
  var setSavingSupervisors = savingSupervisorsState[1];
  
  // ============================================================================
  // AVATAR MODE STATE (persisted in app_settings)
  // ============================================================================
  // 'role' = initials + role color (default)
  // 'photo' = round profile photo (fallback to role mode per user)
  // 'personality' = initials + personality color (fallback to role color)
  var avatarModeState = useState('role');
  var avatarMode = avatarModeState[0];
  var setAvatarMode = avatarModeState[1];

  // Profile data cache (personality + photos) keyed by userId
  var profileCacheState = useState({});
  var profileCache = profileCacheState[0];
  var setProfileCache = profileCacheState[1];

  // Load avatar mode preference from Supabase app_settings on mount
  useEffect(function() {
    supabase.from('app_settings').select('value').eq('key', 'hfbAvatarMode').maybeSingle()
      .then(function(res) {
        if (res.data && res.data.value) {
          var mode = typeof res.data.value === 'string' ? res.data.value : res.data.value;
          if (['role', 'photo', 'personality'].indexOf(mode) !== -1) {
            setAvatarMode(mode);
          }
        }
      });
  }, []);

  // Load user profile data for avatar modes (personality + photos)
  // Resolves storage paths to signed URLs for photo display
  useEffect(function() {
    supabase.from('user_profiles')
      .select('user_id, personality_test_result, profile_photo_round')
      .then(function(res) {
        if (!res.data) return;
        var cache = {};
        var photoPromises = [];
        res.data.forEach(function(row) {
          cache[row.user_id] = {
            personalityTestResult: row.personality_test_result || '',
            profilePhotoRound: '', // will be filled after signed URL resolution
            _storagePath: row.profile_photo_round || '',
          };
          if (row.profile_photo_round && !row.profile_photo_round.startsWith('data:')) {
            photoPromises.push(
              supabase.storage.from('profile-photos')
                .createSignedUrl(row.profile_photo_round, 3600)
                .then(function(urlRes) {
                  if (urlRes.data) {
                    cache[row.user_id].profilePhotoRound = urlRes.data.signedUrl;
                  }
                })
                .catch(function() { /* ignore failed URL resolution */ })
            );
          } else if (row.profile_photo_round) {
            // Backward compat: base64 data URL
            cache[row.user_id].profilePhotoRound = row.profile_photo_round;
          }
        });
        if (photoPromises.length > 0) {
          Promise.all(photoPromises).then(function() {
            setProfileCache(Object.assign({}, cache));
          });
        } else {
          setProfileCache(cache);
        }
      });
  }, []);

  // Persist avatar mode to Supabase
  var handleSetAvatarMode = useCallback(function(mode) {
    setAvatarMode(mode);
    supabase.from('app_settings')
      .upsert({ key: 'hfbAvatarMode', value: mode }, { onConflict: 'key' })
      .then(function() { /* fire and forget */ });
  }, []);

  var activePopoverState = useState(null);
  var activePopoverId = activePopoverState[0];
  var setActivePopoverId = activePopoverState[1];

  var expandedAllocState = useState(null); // userId whose +N allocations are expanded
  var expandedAllocUserId = expandedAllocState[0];
  var setExpandedAllocUserId = expandedAllocState[1];

  // Editing allocation state: { id, projectId, percentage } or null
  var editingAllocationState = useState(null);
  var editingAllocation = editingAllocationState[0];
  var setEditingAllocation = editingAllocationState[1];

  var popoverAnchorState = useState(null);
  var popoverAnchor = popoverAnchorState[0];
  var setPopoverAnchor = popoverAnchorState[1];

  var activeDragState = useState(null);
  var activeDragId = activeDragState[0];
  var setActiveDragId = activeDragState[1];

  var confirmDialogState = useState(null);
  var confirmDialog = confirmDialogState[0];
  var setConfirmDialog = confirmDialogState[1];

  var copyWeekModalState = useState(false);
  var showCopyWeekModal = copyWeekModalState[0];
  var setShowCopyWeekModal = copyWeekModalState[1];

  var copyingWeekState = useState(false);
  var copyingWeek = copyingWeekState[0];
  var setCopyingWeek = copyingWeekState[1];

  var copyResultState = useState(null);
  var copyResult = copyResultState[0];
  var setCopyResult = copyResultState[1];

  var containerRef = useRef(null);

  // Local state initialized from Supabase
  var localAssignmentsState = useState([]);
  var localAssignments = localAssignmentsState[0];
  var setLocalAssignments = localAssignmentsState[1];

  var localAllocationsState = useState([]);
  var localAllocations = localAllocationsState[0];
  var setLocalAllocations = localAllocationsState[1];

  var prevStateRef = useRef({ assignments: [], allocations: [] });

  // ============================================================================
  // NEW: Zoom + Pan Canvas State
  // ============================================================================
  var canvasRef = useRef(null);
  var boardContentRef = useRef(null); // Phase 4.6: Ref to measure board content width
  var scrollContainerRef = useRef(null); // Phase 4.6: Ref for scroll mode container
  var isPanningRef = useRef(false);
  var panStartRef = useRef({ x: 0, y: 0 });
  var lastOffsetRef = useRef({ x: 0, y: 0 });
  var hasInitializedRef = useRef(false); // Phase 4.6: Track if initial centering done
  
  var zoomState = useState(1);
  var zoom = zoomState[0];
  var setZoom = zoomState[1];
  
  var panOffsetState = useState({ x: 0, y: 0 });
  var panOffset = panOffsetState[0];
  var setPanOffset = panOffsetState[1];
  
  var scrollModeState = useState(false);
  var scrollMode = scrollModeState[0];
  var setScrollMode = scrollModeState[1];
  
  // Phase 4.6: Track board area height for full-height droppable columns
  var boardHeightState = useState(400);
  var boardHeight = boardHeightState[0];
  var setBoardHeight = boardHeightState[1];
  
  // Phase 4.7: Dynamic footer height measurement
  var stickyFooterRef = useRef(null);
  var footerHeightState = useState(180); // Default estimate
  var footerHeight = footerHeightState[0];
  var setFooterHeight = footerHeightState[1];
  
  var ZOOM_MIN = 0.6;
  var ZOOM_MAX = 1.6;
  var ZOOM_STEP = 0.1;
  
  // Phase 4.7: Measure sticky footer height on mount, resize, visibility, and element resize
  useEffect(function() {
    function measureFooter() {
      if (stickyFooterRef.current) {
        var height = stickyFooterRef.current.offsetHeight;
        if (height > 0) {
          setFooterHeight(function(prev) { return prev !== height ? height : prev; });
        }
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        measureFooter();
      }
    }

    // Initial measurement via rAF + safety-net timeout
    var rafId = requestAnimationFrame(measureFooter);
    var timer = setTimeout(measureFooter, 100);

    // Re-measure on window resize and focus (returning to tab)
    window.addEventListener('resize', measureFooter);
    window.addEventListener('focus', measureFooter);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // ResizeObserver for ongoing footer size changes
    var observer = null;
    if (typeof ResizeObserver !== 'undefined' && stickyFooterRef.current) {
      observer = new ResizeObserver(measureFooter);
      observer.observe(stickyFooterRef.current);
    }

    return function() {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
      window.removeEventListener('resize', measureFooter);
      window.removeEventListener('focus', measureFooter);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (observer) { observer.disconnect(); }
    };
  }, [isFullScreen, scrollMode]);

  // DATA HOOKS - Phase 4.5: Use combined hook with refreshWeekSnapshot
  var weekHook = useHFBWeek(currentWeekId);
  var dataHook = useHFBData(currentWeekId, false); // Set second param to true for realtime
  var actions = useHFBActions();
  var roleTargetActions = useHFBRoleTargetActions();

  var supabaseAssignments = dataHook.assignments;
  var supabaseAllocations = dataHook.allocations;
  var refreshWeekSnapshot = dataHook.refreshWeekSnapshot;
  var isSaving = dataHook.isSaving;
  var setIsSaving = dataHook.setIsSaving;

  // Active project IDs for role targets fetching
  var activeProjectIds = useMemo(function() {
    return projects
      .filter(function(p) { return p.status === 'active'; })
      .map(function(p) { return p.id; });
  }, [projects]);

  // Active projects list for the allocation wheel picker
  var activeProjects = useMemo(function() {
    return (projects || []).filter(function(p) { return p && p.status === 'active'; });
  }, [projects]);

  // Role targets hook - fetches targets for active projects and current week
  var roleTargetsHook = useHFBRoleTargets(currentWeekId, activeProjectIds);
  var roleTargets = roleTargetsHook.targets;
  var roleTargetsLoading = roleTargetsHook.isLoading;
  var setRoleTargets = roleTargetsHook.setTargets;
  var refetchRoleTargets = roleTargetsHook.refetch;

  // ============================================================================
  // MODEL DEMAND OVERLAY
  // ============================================================================
  var showModelDemandState = useState(false);
  var showModelDemand = showModelDemandState[0];
  var setShowModelDemand = showModelDemandState[1];

  // Compute weekStart date string for the current week (needed by model demand hook)
  var currentWeekStart = useMemo(function() {
    var parts = currentWeekId.split('-W');
    var year = parseInt(parts[0], 10);
    var week = parseInt(parts[1], 10);
    var monday = getMondayOfISOWeek(year, week);
    return monday.toISOString().split('T')[0];
  }, [currentWeekId]);

  // Single-element array: just the visible week for model demand
  var modelDemandWeeks = useMemo(function() {
    return [currentWeekStart];
  }, [currentWeekStart]);

  var modelDemandHook = useHFBModelDemand(showModelDemand, modelDemandWeeks);
  var modelDemand = modelDemandHook.demand;          // { weekStart: { roleInitial: fte } }
  var modelDemandLoading = modelDemandHook.isLoading;
  var modelDemandNoData = modelDemandHook.noData;
  var modelDemandError = modelDemandHook.error;
  var modelDemandRunInfo = modelDemandHook.runInfo;   // { id, created_at, horizon_months, status } | null

  // Get model demand for the current week as a flat { roleInitial: fte } map
  var currentWeekModelDemand = useMemo(function() {
    if (!showModelDemand) return {};
    return modelDemand[currentWeekStart] || {};
  }, [showModelDemand, modelDemand, currentWeekStart]);

  // Sync local state from Supabase
  useEffect(function() {
    debugLog('Syncing assignments from Supabase - weekId:', currentWeekId, 'count:', supabaseAssignments.length);
    setLocalAssignments(supabaseAssignments);
  }, [supabaseAssignments, currentWeekId]);

  useEffect(function() {
    debugLog('Syncing allocations from Supabase - weekId:', currentWeekId, 'count:', supabaseAllocations.length);
    setLocalAllocations(supabaseAllocations);
  }, [supabaseAllocations, currentWeekId]);

  var assignments = localAssignments;
  var allocations = localAllocations;

  var isLoading = weekHook.isLoading || dataHook.isLoading;
  var dataErrors = dataHook.errors;

  // Phase 4.5: Memoized lookup maps for performance
  var projectsById = useMemo(function() {
    var map = new Map();
    projects.forEach(function(p) { map.set(p.id, p); map.set(String(p.id), p); });
    return map;
  }, [projects]);

  var teamsById = useMemo(function() {
    var map = new Map();
    teams.forEach(function(t) { map.set(t.id, t); map.set(String(t.id), t); });
    return map;
  }, [teams]);

  var usersById = useMemo(function() {
    var map = new Map();
    users.forEach(function(u) { map.set(u.id, u); map.set(String(u.id), u); });
    return map;
  }, [users]);

  var sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  var isAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'admin-pa');
  var isSupervisor = currentUser && currentUser.role === 'supervisor';

  // Past weeks are read-only for everyone EXCEPT admins. Admins may retroactively
  // edit team assignments, allocations and role targets for past weeks — e.g. to
  // fix a missed project assignment so the person can still log hours for that
  // week. `isPastWeek` remains the "is this week historical?" flag used for
  // display; `isLockedWeek` is the effective edit lock and is false for admins.
  var canEditPastWeeks = !!isAdmin;
  var isLockedWeek = isPastWeek && !canEditPastWeeks;

  var isTeamSupervisor = useCallback(function(teamId) {
    if (!currentUser || !teamId) return false;
    var team = teams.find(function(t) { return t.id === teamId || String(t.id) === String(teamId); });
    if (!team) return false;
    var supervisorIds = team.supervisorIds || [];
    return supervisorIds.some(function(sid) { return String(sid) === String(currentUser.id); });
  }, [currentUser, teams]);
  
  // RBAC: Admins and Supervisors have FULL access to manage ANY team
  // Only 'user' role is read-only
  var canManageTeam = useCallback(function(teamId) {
    if (isAdmin) return true;
    if (isSupervisor) return true;  // Supervisors can manage ALL teams
    return false;  // Regular users cannot manage teams
  }, [isAdmin, isSupervisor]);
  
  // Check if current user can edit role targets (supervisors and admins only)
  var canEditRoleTargets = useMemo(function() {
    if (isAdmin) return true;
    if (isSupervisor) return true;  // All supervisors can edit role targets
    return false;
  }, [isAdmin, isSupervisor]);
  
  var isUserSupervisorOfAnyTeam = useCallback(function(userId) {
    return teams.some(function(team) {
      var supervisorIds = team.supervisorIds || [];
      return supervisorIds.some(function(sid) { return String(sid) === String(userId); });
    });
  }, [teams]);
  
  var isUserSupervisorOfTeam = useCallback(function(userId, teamId) {
    var team = teams.find(function(t) { return t.id === teamId || String(t.id) === String(teamId); });
    if (!team) return false;
    var supervisorIds = team.supervisorIds || [];
    return supervisorIds.some(function(sid) { return String(sid) === String(userId); });
  }, [teams]);

  var getUserTeamAssignment = useCallback(function(userId) {
    var userIdStr = String(userId);
    return assignments.find(function(a) { return String(a.userId) === userIdStr; });
  }, [assignments]);

  var getUserAllocations = useCallback(function(userId) {
    var userIdStr = String(userId);
    return allocations.filter(function(a) { return String(a.userId) === userIdStr; });
  }, [allocations]);

  var getUserTotalAllocation = useCallback(function(userId) {
    return getUserAllocations(userId).reduce(function(sum, a) { return sum + a.percentage; }, 0);
  }, [getUserAllocations]);

  var getTeamById = useCallback(function(teamId) {
    if (teamId === null || teamId === undefined) return null;
    var teamIdNum = Number(teamId);
    return teams.find(function(t) { return Number(t.id) === teamIdNum; });
  }, [teams]);

  var getProjectById = useCallback(function(projectId) {
    if (projectId === null || projectId === undefined) return null;
    var projectIdNum = Number(projectId);
    return projects.find(function(p) { return Number(p.id) === projectIdNum; });
  }, [projects]);

  var activeUsers = useMemo(function() {
    return users.filter(function(u) { return u.isActive !== false; });
  }, [users]);

  var activeTeams = useMemo(function() {
    return teams.filter(function(t) { return t.isActive !== false; });
  }, [teams]);

  // When focused on a team, only show that team
  var displayTeams = useMemo(function() {
    if (!focusedTeamId) return activeTeams;
    return activeTeams.filter(function(t) { return t.id === focusedTeamId; });
  }, [activeTeams, focusedTeamId]);

  // Categorize users
  var userCategories = useMemo(function() {
    var byTeam = new Map();
    var unassigned = [];
    var floating = [];
    
    activeTeams.forEach(function(team) { byTeam.set(team.id, []); });
    
    activeUsers.forEach(function(user) {
      var assignment = assignments.find(function(a) { return String(a.userId) === String(user.id); });
      var isFloatingRole = FLOATING_ROLES.indexOf(user.jobRole) !== -1;
      
      if (assignment) {
        var teamId = assignment.teamId;
        if (teamId === null || teamId === undefined) {
          floating.push(user);
        } else {
          var arr = byTeam.get(teamId);
          if (arr) {
            arr.push(user);
          } else {
            if (isFloatingRole) {
              floating.push(user);
            } else {
              unassigned.push(user);
            }
          }
        }
      } else {
        if (isFloatingRole) {
          floating.push(user);
        } else {
          unassigned.push(user);
        }
      }
    });
    
    // Sort all lists by role seniority (most senior first)
    byTeam.forEach(function(arr, key) { arr.sort(compareByRoleSeniority); });
    unassigned.sort(compareByRoleSeniority);
    floating.sort(compareByRoleSeniority);
    
    return { byTeam: byTeam, unassigned: unassigned, floating: floating };
  }, [activeUsers, activeTeams, assignments]);

  var activeDragUser = useMemo(function() {
    if (!activeDragId) return null;
    var rawUserId = String(activeDragId).replace('user:', '');
    return activeUsers.find(function(u) { return String(u.id) === rawUserId; });
  }, [activeDragId, activeUsers]);

  var isDragging = activeDragId !== null;

  // ============================================================================
  // Phase 4.6: Auto-center and Auto-fit functions
  // ============================================================================
  
  // Calculate centered pan offset for Canvas mode
  var calculateCenteredOffset = useCallback(function(zoomLevel) {
    if (!canvasRef.current || !boardContentRef.current) return { x: 0, y: 0 };
    
    var viewportWidth = canvasRef.current.clientWidth;
    var contentWidth = boardContentRef.current.scrollWidth;
    
    // Center horizontally: (viewport - content*zoom) / 2
    var centeredX = (viewportWidth - contentWidth * zoomLevel) / 2;
    // Clamp so we don't push content off-screen to the right
    centeredX = Math.min(centeredX, 0);
    
    return { x: centeredX, y: 0 };
  }, []);
  
  // Calculate zoom to fit content in viewport
  var calculateFitZoom = useCallback(function() {
    if (!canvasRef.current || !boardContentRef.current) return 1;
    
    var viewportWidth = canvasRef.current.clientWidth;
    var contentWidth = boardContentRef.current.scrollWidth;
    
    if (contentWidth === 0) return 1;
    
    // Calculate zoom to fit width (leave some padding)
    var fitZoom = (viewportWidth - 40) / contentWidth;
    
    // Clamp to zoom limits
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, fitZoom));
  }, []);
  
  // Center scroll container for Scroll mode
  var centerScrollContainer = useCallback(function() {
    if (!scrollContainerRef.current || !boardContentRef.current) return;
    
    var containerWidth = scrollContainerRef.current.clientWidth;
    var contentWidth = boardContentRef.current.scrollWidth;
    
    if (contentWidth > containerWidth) {
      var centeredScrollLeft = (contentWidth - containerWidth) / 2;
      scrollContainerRef.current.scrollLeft = Math.max(0, centeredScrollLeft);
    }
  }, []);
  
  // Initialize view (auto-fit + center) for Canvas mode
  var initializeCanvasView = useCallback(function() {
    if (!canvasRef.current || !boardContentRef.current) return;
    
    // Wait for content to be measured
    requestAnimationFrame(function() {
      var fitZoom = calculateFitZoom();
      var centeredOffset = calculateCenteredOffset(fitZoom);
      
      setZoom(fitZoom);
      setPanOffset(centeredOffset);
    });
  }, [calculateFitZoom, calculateCenteredOffset]);
  
  // Reset view - center at zoom 1 (or auto-fit if content is larger)
  var handleResetView = useCallback(function() {
    if (scrollMode) {
      setPanOffset({ x: 0, y: 0 });
      centerScrollContainer();
    } else {
      initializeCanvasView();
    }
  }, [scrollMode, initializeCanvasView, centerScrollContainer]);
  
  // Phase 4.6: Auto-center/fit on mount, resize, mode toggle, fullscreen toggle
  useEffect(function() {
    if (hasInitializedRef.current) return;
    
    // Delay to allow DOM to render
    var timer = setTimeout(function() {
      if (scrollMode) {
        centerScrollContainer();
      } else {
        initializeCanvasView();
      }
      hasInitializedRef.current = true;
    }, 100);
    
    return function() { clearTimeout(timer); };
  }, [scrollMode, initializeCanvasView, centerScrollContainer]);
  
  // Re-center on mode toggle
  useEffect(function() {
    if (!hasInitializedRef.current) return;
    
    var timer = setTimeout(function() {
      if (scrollMode) {
        setPanOffset({ x: 0, y: 0 });
        centerScrollContainer();
      } else {
        initializeCanvasView();
      }
    }, 50);
    
    return function() { clearTimeout(timer); };
  }, [scrollMode, isFullScreen]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update board height on resize
  useEffect(function() {
    function updateBoardHeight() {
      if (canvasRef.current) {
        var height = canvasRef.current.clientHeight;
        setBoardHeight(height > 0 ? height : 400);
      }
    }
    
    updateBoardHeight();
    
    var resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && canvasRef.current) {
      resizeObserver = new ResizeObserver(function() {
        updateBoardHeight();
        // Re-center on resize
        if (!scrollMode) {
          var centeredOffset = calculateCenteredOffset(zoom);
          setPanOffset(centeredOffset);
        }
      });
      resizeObserver.observe(canvasRef.current);
    }
    
    window.addEventListener('resize', updateBoardHeight);
    
    return function() {
      window.removeEventListener('resize', updateBoardHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [scrollMode, zoom, calculateCenteredOffset]);

  // ============================================================================
  // NEW: Canvas Handlers (after isDragging is defined)
  // ============================================================================
  
  // Toggle scroll mode - Phase 4.6: Don't call handleResetView, useEffect handles re-centering
  var handleToggleScrollMode = useCallback(function() {
    hasInitializedRef.current = false; // Reset so we re-center
    setScrollMode(function(prev) { return !prev; });
  }, []);
  
  // Zoom in
  var handleZoomIn = useCallback(function() {
    setZoom(function(prev) { return Math.min(ZOOM_MAX, prev + ZOOM_STEP); });
  }, []);
  
  // Zoom out
  var handleZoomOut = useCallback(function() {
    setZoom(function(prev) { return Math.max(ZOOM_MIN, prev - ZOOM_STEP); });
  }, []);
  
  // Handle wheel zoom (Ctrl/Cmd + wheel or pinch)
  var handleCanvasWheel = useCallback(function(e) {
    if (scrollMode || isDragging) return;
    
    // Only zoom if Ctrl/Cmd is held or it's a pinch gesture (ctrlKey is true for pinch on trackpad)
    if (!e.ctrlKey && !e.metaKey) return;
    
    e.preventDefault();
    
    var rect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
    if (!rect) return;
    
    // Cursor position relative to canvas
    var cursorX = e.clientX - rect.left;
    var cursorY = e.clientY - rect.top;
    
    // Calculate new zoom
    var delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    var newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom + delta));
    
    if (newZoom === zoom) return;
    
    // Zoom centered on cursor position
    var zoomRatio = newZoom / zoom;
    var newOffsetX = cursorX - (cursorX - panOffset.x) * zoomRatio;
    var newOffsetY = cursorY - (cursorY - panOffset.y) * zoomRatio;
    
    setZoom(newZoom);
    setPanOffset({ x: newOffsetX, y: newOffsetY });
  }, [scrollMode, isDragging, zoom, panOffset]);
  
  // Handle pan start - Phase 4.6: Only pan in Canvas mode, not Scroll mode
  var handleCanvasMouseDown = useCallback(function(e) {
    // Phase 4.6: Don't pan in scroll mode
    if (scrollMode) return;
    
    // Don't pan while dragging users
    if (isDragging) return;
    
    // Only pan on left click
    if (e.button !== 0) return;
    
    // Don't pan if clicking on interactive elements
    if (e.target.closest('[data-popover-trigger]') || 
        e.target.closest('button') ||
        e.target.closest('[draggable]') ||
        e.target.closest('.cursor-pointer')) return;
    
    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    lastOffsetRef.current = { x: panOffset.x, y: panOffset.y };
    
    document.body.style.cursor = 'grabbing';
    e.preventDefault();
  }, [scrollMode, isDragging, panOffset]);
  
  // Handle pan move
  var handleCanvasMouseMove = useCallback(function(e) {
    if (!isPanningRef.current) return;
    
    var dx = e.clientX - panStartRef.current.x;
    var dy = e.clientY - panStartRef.current.y;
    
    setPanOffset({
      x: lastOffsetRef.current.x + dx,
      y: lastOffsetRef.current.y + dy
    });
  }, []);
  
  // Handle pan end
  var handleCanvasMouseUp = useCallback(function() {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      document.body.style.cursor = '';
    }
  }, []);
  
  // Global mouse up listener for pan
  useEffect(function() {
    document.addEventListener('mouseup', handleCanvasMouseUp);
    document.addEventListener('mousemove', handleCanvasMouseMove);
    return function() {
      document.removeEventListener('mouseup', handleCanvasMouseUp);
      document.removeEventListener('mousemove', handleCanvasMouseMove);
    };
  }, [handleCanvasMouseUp, handleCanvasMouseMove]);
  
  // ESC key to reset view
  useEffect(function() {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && !scrollMode && (zoom !== 1 || panOffset.x !== 0 || panOffset.y !== 0)) {
        handleResetView();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return function() { document.removeEventListener('keydown', handleKeyDown); };
  }, [scrollMode, zoom, panOffset, handleResetView]);
  
  // Check for reduced motion preference
  useEffect(function() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      var mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (mediaQuery.matches) {
        setScrollMode(true);
      }
    }
  }, []);

  function handlePreviousWeek() {
    setCurrentWeekId(function(prev) { return getAdjacentWeek(prev, -1); });
    setCopyResult(null);
    setActivePopoverId(null);
  }

  function handleNextWeek() {
    setCurrentWeekId(function(prev) { return getAdjacentWeek(prev, 1); });
    setCopyResult(null);
    setActivePopoverId(null);
  }

  function handleGoToCurrentWeek() {
    setCurrentWeekId(getCurrentISOWeek());
    setCopyResult(null);
    setActivePopoverId(null);
  }

  function rollbackState() {
    debugLog('Rolling back to previous state');
    setLocalAssignments(prevStateRef.current.assignments);
    setLocalAllocations(prevStateRef.current.allocations);
  }

  function saveStateForRollback() {
    prevStateRef.current = {
      assignments: localAssignments.slice(),
      allocations: localAllocations.slice()
    };
  }

  function handleDragStart(event) {
    debugLog('dragStart id=' + event.active.id);
    setActiveDragId(event.active.id);
    setActivePopoverId(null);
    // Add dragging class to prevent text selection / magnifying glass
    injectDraggingStyles();
    document.body.classList.add('hfb-dragging');
  }

  async function handleDragEnd(event) {
    var active = event.active;
    var over = event.over;
    
    setActiveDragId(null);
    // Remove dragging class
    document.body.classList.remove('hfb-dragging');

    if (!over) return;
    if (isLockedWeek) {
      addToast('Past weeks are read-only. Only admins can edit them.', 'warning');
      return;
    }

    var rawActiveId = String(active.id);
    var rawOverId = String(over.id);
    
    debugLog('handleDragEnd - active:', rawActiveId, 'over:', rawOverId);
    
    if (rawActiveId.indexOf('user:') !== 0) return;
    var rawUserId = rawActiveId.replace('user:', '');

    var isDropOnTeam = rawOverId.indexOf('team:') === 0;
    var isDropOnUnassigned = rawOverId === 'pool:unassigned';
    var isDropOnFloating = rawOverId === 'pool:floating';

    if (!isDropOnTeam && !isDropOnUnassigned && !isDropOnFloating) {
      debugLog('Drop target not recognized:', rawOverId);
      return;
    }

    var user = activeUsers.find(function(u) { return String(u.id) === rawUserId; });
    if (!user) return;

    var currentAssignment = getUserTeamAssignment(rawUserId);
    var fromTeamId = currentAssignment ? currentAssignment.teamId : null;
    
    var toTeamId = null;
    var targetType = 'unassigned';
    
    if (isDropOnTeam) {
      var rawTeamId = rawOverId.replace('team:', '');
      toTeamId = Number(rawTeamId);
      targetType = 'team';
    } else if (isDropOnFloating) {
      toTeamId = null;
      targetType = 'floating';
    } else if (isDropOnUnassigned) {
      toTeamId = null;
      targetType = 'unassigned';
    }

    debugLog('Move user', user.name, 'from team', fromTeamId, 'to', targetType, toTeamId);

    if (targetType === 'team' && fromTeamId !== null && Number(fromTeamId) === toTeamId) {
      debugLog('Same team, no change needed');
      return;
    }
    
    if (targetType === 'floating' && currentAssignment && currentAssignment.teamId === null) {
      debugLog('Already floating, no change needed');
      return;
    }
    
    if (targetType === 'unassigned' && !currentAssignment) {
      debugLog('Already unassigned, no change needed');
      return;
    }

    // RBAC: Only 'user' role is restricted from team moves
    // Admins and Supervisors have full access to manage ANY team
    if (!isAdmin && !isSupervisor && targetType === 'team') {
      addToast('You do not have permission to move users between teams', 'warning');
      return;
    }

    var userAllocations = getUserAllocations(rawUserId);
    var hasAllocations = userAllocations.length > 0;
    var willClearAllocations = hasAllocations && (targetType !== 'team' || (fromTeamId !== null && Number(fromTeamId) !== toTeamId));

    if (willClearAllocations) {
      var fromTeam = getTeamById(fromTeamId);
      var toTeam = toTeamId !== null ? getTeamById(toTeamId) : null;
      var targetName = targetType === 'floating' ? 'Floating' : 
                       targetType === 'unassigned' ? 'Unassigned' :
                       (toTeam ? toTeam.name : 'Unknown');
      var message = 'Moving ' + user.name + ' to ' + targetName + ' will clear their ' + userAllocations.length + ' allocation(s). Continue?';
      
      setConfirmDialog({
        message: message,
        onConfirm: function() {
          setConfirmDialog(null);
          executeMove(user, rawUserId, fromTeamId, toTeamId, targetType);
        },
        onCancel: function() {
          setConfirmDialog(null);
        }
      });
      return;
    }

    executeMove(user, rawUserId, fromTeamId, toTeamId, targetType);
  }

  async function executeMove(user, rawUserId, fromTeamId, toTeamId, targetType) {
    debugLog('executeMove:', { user: user.name, fromTeamId: fromTeamId, toTeamId: toTeamId, targetType: targetType });
    
    saveStateForRollback();
    setIsSaving(true);

    if (targetType === 'unassigned') {
      setLocalAssignments(function(prev) {
        return prev.filter(function(a) { return String(a.userId) !== rawUserId; });
      });
      setLocalAllocations(function(prev) {
        return prev.filter(function(a) { return String(a.userId) !== rawUserId; });
      });
    } else if (targetType === 'floating') {
      setLocalAssignments(function(prev) {
        var filtered = prev.filter(function(a) { return String(a.userId) !== rawUserId; });
        var newAssignment = {
          id: 'optimistic-' + Date.now(),
          weekId: currentWeekId,
          userId: user.id,
          teamId: null
        };
        return filtered.concat([newAssignment]);
      });
      if (fromTeamId !== null) {
        setLocalAllocations(function(prev) {
          return prev.filter(function(a) { return String(a.userId) !== rawUserId; });
        });
      }
    } else {
      setLocalAssignments(function(prev) {
        var filtered = prev.filter(function(a) { return String(a.userId) !== rawUserId; });
        var newAssignment = {
          id: 'optimistic-' + Date.now(),
          weekId: currentWeekId,
          userId: user.id,
          teamId: toTeamId
        };
        return filtered.concat([newAssignment]);
      });
      if (fromTeamId !== null && Number(fromTeamId) !== toTeamId) {
        setLocalAllocations(function(prev) {
          return prev.filter(function(a) { return String(a.userId) !== rawUserId; });
        });
      }
    }

    try {
      var result;
      if (targetType === 'unassigned') {
        result = await actions.unassignFromTeamAndResetAllocations({
          weekId: currentWeekId,
          userId: user.id
        });
      } else if (targetType === 'floating') {
        result = await actions.assignToFloating({
          weekId: currentWeekId,
          userId: user.id,
          previousTeamId: fromTeamId
        });
      } else {
        result = await actions.assignTeamAndResetAllocations({
          weekId: currentWeekId,
          userId: user.id,
          teamId: toTeamId,
          previousTeamId: fromTeamId
        });
      }

      if (result.success) {
        var actionName = targetType === 'unassigned' ? 'Unassigned' : 
                        targetType === 'floating' ? 'Set to Floating' : 
                        'Team assigned';
        addToast(actionName + ': ' + user.name, 'success');
        await refreshWeekSnapshot();
      } else {
        // Debug log for failed persistence (helps identify RLS vs UI issues)
        console.error('[HFB] Move failed:', {
          targetType: targetType,
          fromTeamId: fromTeamId,
          toTeamId: toTeamId,
          userId: rawUserId,
          error: result.error
        });
        addToast('Failed to save: ' + result.error, 'error');
        rollbackState();
      }
    } catch (err) {
      addToast('Error saving: ' + (err.message || 'Unknown error'), 'error');
      rollbackState();
    } finally {
      setIsSaving(false);
    }
  }

  function handleDragCancel() {
    setActiveDragId(null);
    // Remove dragging class
    document.body.classList.remove('hfb-dragging');
  }

  // Allocation handlers
  async function handleAddAllocation(userId, data) {
    if (isLockedWeek) return;

    var currentTotal = getUserTotalAllocation(userId);
    var newTotal = currentTotal + data.percentage;
    
    if (newTotal > 100) {
      addToast('Cannot exceed 100% allocation', 'warning');
      return;
    }

    debugLog('Adding allocation - userId:', userId, 'projectId:', data.projectId, 'percentage:', data.percentage);
    setIsSaving(true);

    var optimisticId = 'optimistic-' + Date.now();
    setLocalAllocations(function(prev) {
      return prev.concat([{
        id: optimisticId,
        weekId: currentWeekId,
        userId: userId,
        projectId: parseInt(data.projectId, 10),
        percentage: data.percentage
      }]);
    });

    try {
      var result = await actions.addAllocation({
        weekId: currentWeekId,
        userId: userId,
        projectId: parseInt(data.projectId, 10),
        percentage: data.percentage,
        notes: ''
      });

      if (result.success) {
        debugLog('Allocation added successfully, refetching...');
        addToast('Allocation added', 'success');
        await refreshWeekSnapshot();
      } else {
        addToast('Failed to add allocation: ' + result.error, 'error');
        setLocalAllocations(function(prev) {
          return prev.filter(function(a) { return a.id !== optimisticId; });
        });
      }
    } catch (err) {
      addToast('Error adding allocation: ' + (err.message || 'Unknown error'), 'error');
      setLocalAllocations(function(prev) {
        return prev.filter(function(a) { return a.id !== optimisticId; });
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleUpdateAllocation(allocationId, data) {
    if (isLockedWeek) return;

    debugLog('Updating allocation - id:', allocationId, 'percentage:', data.percentage);

    var currentAlloc = allocations.find(function(a) { return a.id === allocationId; });
    if (!currentAlloc) return;

    var userId = currentAlloc.userId;
    var otherTotal = getUserAllocations(userId)
      .filter(function(a) { return a.id !== allocationId; })
      .reduce(function(sum, a) { return sum + a.percentage; }, 0);
    
    if (otherTotal + data.percentage > 100) {
      addToast('Cannot exceed 100% allocation', 'warning');
      return;
    }

    setIsSaving(true);
    var oldPercentage = currentAlloc.percentage;
    setLocalAllocations(function(prev) {
      return prev.map(function(a) {
        if (a.id === allocationId) {
          return { ...a, percentage: data.percentage };
        }
        return a;
      });
    });

    try {
      var result = await actions.updateAllocation(allocationId, {
        percentage: data.percentage
      });

      if (result.success) {
        debugLog('Allocation updated successfully, refetching...');
        addToast('Allocation updated', 'success');
        await refreshWeekSnapshot();
      } else {
        addToast('Failed to update allocation: ' + result.error, 'error');
        setLocalAllocations(function(prev) {
          return prev.map(function(a) {
            if (a.id === allocationId) {
              return { ...a, percentage: oldPercentage };
            }
            return a;
          });
        });
      }
    } catch (err) {
      addToast('Error updating allocation: ' + (err.message || 'Unknown error'), 'error');
      await refreshWeekSnapshot();
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRemoveAllocation(allocationId) {
    if (isLockedWeek) return;

    debugLog('Removing allocation - id:', allocationId);
    setIsSaving(true);

    var removedAlloc = allocations.find(function(a) { return a.id === allocationId; });
    setLocalAllocations(function(prev) {
      return prev.filter(function(a) { return a.id !== allocationId; });
    });

    try {
      var result = await actions.removeAllocation(allocationId);
      if (result.success) {
        debugLog('Allocation removed successfully, refetching...');
        addToast('Allocation removed', 'success');
        await refreshWeekSnapshot();
      } else {
        addToast('Failed to remove allocation: ' + result.error, 'error');
        if (removedAlloc) {
          setLocalAllocations(function(prev) { return prev.concat([removedAlloc]); });
        }
      }
    } catch (err) {
      addToast('Error removing allocation: ' + (err.message || 'Unknown error'), 'error');
      if (removedAlloc) {
        setLocalAllocations(function(prev) { return prev.concat([removedAlloc]); });
      }
    } finally {
      setIsSaving(false);
    }
  }

  // Role target change handler - upserts to Supabase
  async function handleRoleTargetChange(projectId, roleInitial, targetFte) {
    if (isLockedWeek) return;
    if (!canEditRoleTargets) {
      addToast('Only supervisors and admins can edit targets', 'warning');
      return;
    }

    debugLog('Updating role target - projectId:', projectId, 'role:', roleInitial, 'targetFte:', targetFte);

    // Optimistically update local state
    setRoleTargets(function(prev) {
      var updated = { ...prev };
      var pid = String(projectId);
      if (!updated[pid]) {
        updated[pid] = {};
      }
      updated[pid][roleInitial] = {
        id: updated[pid][roleInitial] ? updated[pid][roleInitial].id : null,
        targetFte: targetFte,
        updatedBy: currentUser ? currentUser.id : null,
        updatedAt: new Date().toISOString()
      };
      return updated;
    });

    try {
      var result = await roleTargetActions.upsertRoleTarget({
        weekId: currentWeekId,
        projectId: projectId,
        roleInitial: roleInitial,
        targetFte: targetFte,
        updatedBy: currentUser ? currentUser.id : null
      });

      if (!result.success) {
        addToast('Failed to save target: ' + result.error, 'error');
        // Refetch to restore correct state
        refetchRoleTargets();
      }
    } catch (err) {
      addToast('Error saving target: ' + (err.message || 'Unknown error'), 'error');
      refetchRoleTargets();
    }
  }

  function handleOpenCopyWeekModal() {
    if (isLockedWeek) return;
    setCopyResult(null);
    setShowCopyWeekModal(true);
  }

  async function handleCopyWeek(sourceWeekId) {
    if (isLockedWeek) return;
    setCopyingWeek(true);
    setCopyResult(null);
    
    var result = await actions.copyWeek(sourceWeekId, currentWeekId);
    
    setCopyingWeek(false);
    setCopyResult(result);
    
    if (result.success) {
      var message = 'Copied ' + result.copiedTeams + ' assignments, ' + result.copiedAllocations + ' allocations';
      if (result.copiedRoleTargets && result.copiedRoleTargets > 0) {
        message += ', ' + result.copiedRoleTargets + ' role targets';
      }
      addToast(message, 'success');
      setShowCopyWeekModal(false);
      await refreshWeekSnapshot();
      // Also refetch role targets to ensure they're visible
      if (refetchRoleTargets) {
        refetchRoleTargets();
      }
    } else {
      addToast('Copy failed: ' + (result.errors ? result.errors.join(', ') : 'Unknown error'), 'error');
    }
  }

  // ============================================================================
  // SUPERVISOR EDIT HANDLERS
  // ============================================================================
  function handleOpenSupervisorModal(team) {
    if (!isAdmin) {
      addToast('Only admins can edit supervisors', 'warning');
      return;
    }
    
    if (DEBUG_SUPERVISOR) {
      console.log('[HFB] Opening supervisor modal for team:', team.id, team.name);
      console.log('[HFB] Current supervisorIds from props:', team.supervisorIds);
    }
    
    // Initialize selected supervisors from current team
    var currentIds = team.supervisorIds || [];
    setSelectedSupervisors(currentIds.map(function(id) { return String(id); }));
    
    setSupervisorModal({
      teamId: team.id,
      teamName: team.name,
      currentSupervisorIds: currentIds
    });
  }
  
  function handleCloseSupervisorModal() {
    setSupervisorModal(null);
    setSelectedSupervisors([]);
    setSavingSupervisors(false);
  }
  
  function handleToggleSupervisor(userId) {
    var userIdStr = String(userId);
    setSelectedSupervisors(function(prev) {
      if (prev.includes(userIdStr)) {
        return prev.filter(function(id) { return id !== userIdStr; });
      } else {
        return prev.concat([userIdStr]);
      }
    });
  }
  
  async function handleSaveSupervisors() {
    if (!supervisorModal || !onUpdateTeamSupervisors) {
      console.error('[HFB] Cannot save supervisors - callback not available');
      addToast('Cannot save supervisors - callback not available', 'error');
      return;
    }
    
    setSavingSupervisors(true);
    
    if (DEBUG_SUPERVISOR) {
      console.log('[HFB] handleSaveSupervisors BEFORE save:');
      console.log('  - teamId:', supervisorModal.teamId);
      console.log('  - selectedSupervisors (to save):', selectedSupervisors);
      console.log('  - previous supervisorIds:', supervisorModal.currentSupervisorIds);
    }
    
    try {
      // Call the App.js callback to persist to Supabase
      var result = await onUpdateTeamSupervisors(supervisorModal.teamId, selectedSupervisors);
      
      if (DEBUG_SUPERVISOR) {
        console.log('[HFB] onUpdateTeamSupervisors result:', result);
      }
      
      if (result && result.success) {
        addToast('Supervisors updated for ' + supervisorModal.teamName, 'success');
        
        if (DEBUG_SUPERVISOR) {
          // Verify the update by checking the teams prop after a short delay
          setTimeout(function() {
            var updatedTeam = teams.find(function(t) { return String(t.id) === String(supervisorModal.teamId); });
            console.log('[HFB] AFTER save, team.supervisorIds from props:', updatedTeam ? updatedTeam.supervisorIds : 'team not found');
          }, 500);
        }
        
        handleCloseSupervisorModal();
      } else {
        addToast('Failed to update supervisors: ' + (result ? result.error : 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('[HFB] Error saving supervisors:', err);
      addToast('Error saving supervisors: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setSavingSupervisors(false);
    }
  }

  function handleUserClick(user, event) {
    if (isDragging) return;
    event.stopPropagation();

    // Get the bounding rect of the avatar element
    var rect = event.currentTarget.getBoundingClientRect();

    if (activePopoverId === user.id) {
      // Toggle off
      setActivePopoverId(null);
      setPopoverAnchor(null);
      setEditingAllocation(null);
    } else {
      // Open popover — show existing allocations (and allow adding if under 100%)
      setPopoverAnchor(rect);
      setActivePopoverId(user.id);
      setEditingAllocation(null);
    }
  }

  function handleClosePopover() {
    setActivePopoverId(null);
    setPopoverAnchor(null);
    setEditingAllocation(null);
  }

  function handleEditAllocation(userId, alloc) {
    setEditingAllocation({ id: alloc.id, projectId: alloc.projectId, percentage: alloc.percentage });
    setActivePopoverId(userId);
  }

  // ============================================================================
  // FOCUSED VIEW GRID HELPER
  // Returns number of columns based on user count
  // ============================================================================
  function getFocusedGridColumns(count) {
    if (count <= 4) return 2;
    if (count <= 8) return 4;
    if (count <= 12) return 4;
    if (count <= 15) return 5;
    if (count <= 20) return 5;
    if (count <= 24) return 6;
    if (count <= 30) return 6;
    return 7; // 31-35
  }

  // ============================================================================
  // RENDER USER
  // ============================================================================
  function renderCompactUser(user, teamId, teamColor) {
    var userAllocations = getUserAllocations(user.id);
    var totalAllocation = getUserTotalAllocation(user.id);
    var isSupervisor = teamId !== null && isUserSupervisorOfTeam(user.id, teamId);
    var canManage = canManageTeam(teamId);
    var dragDisabled = isLockedWeek || (!isAdmin && !canManage);
    var isPopoverOpen = activePopoverId === user.id;

    var isExpanded = expandedAllocUserId === user.id;
    var visibleAllocations = isExpanded ? userAllocations : userAllocations.slice(0, 3);
    var hiddenCount = userAllocations.length - 3;

    return React.createElement('div', {
      key: user.id,
      className: 'relative flex items-center gap-2 py-2 px-2'
    },
      // User avatar wrapper - relative container for inline popover expansion
      React.createElement('div', {
        className: 'relative inline-flex items-center',
        style: { zIndex: isPopoverOpen ? 99998 : 'auto' }
      },
        // The avatar button - ALWAYS visible, stays in place
        React.createElement(DraggableUser, { user: user, teamId: teamId, disabled: dragDisabled },
          React.createElement('div', {
            'data-popover-trigger': 'true'
          },
            React.createElement(CompactUserAvatar, {
              user: user,
              teamColor: teamColor,
              size: USER_AVATAR_SIZE,
              isSupervisor: isSupervisor,
              isActive: isPopoverOpen,
              totalAllocation: totalAllocation,
              avatarMode: avatarMode,
              profileData: profileCache[user.id],
              onClick: function(e) { handleUserClick(user, e); }
            })
          )
        ),
        // Inline popover - expands AROUND the avatar (no duplicate avatar)
        // Positioned so the "spacer" in the middle aligns with the real avatar
        showAllocations && isPopoverOpen && React.createElement(InlineAllocationPopover, {
          user: user,
          allocations: userAllocations,
          projects: activeProjects,
          totalAllocation: totalAllocation,
          onClose: handleClosePopover,
          onAddAllocation: function(data) { handleAddAllocation(user.id, data); },
          onUpdateAllocation: handleUpdateAllocation,
          onRemoveAllocation: handleRemoveAllocation,
          isPastWeek: isLockedWeek,
          avatarSize: USER_AVATAR_SIZE,
          editingAllocation: editingAllocation
        })
      ),
      // Project icons to the right - only show when showAllocations is true and popover is closed
      showAllocations && !isPopoverOpen && React.createElement('div', { className: 'flex items-center gap-1 flex-wrap' },
        visibleAllocations.map(function(alloc) {
          var project = getProjectById(alloc.projectId);
          return React.createElement(ProjectIcon, {
            key: alloc.id,
            project: project,
            percentage: alloc.percentage,
            size: PROJECT_ICON_SIZE,
            showRemove: !isLockedWeek && canManage,
            onRemove: function() { handleRemoveAllocation(alloc.id); },
            onEdit: (!isLockedWeek && canManage) ? function() { handleEditAllocation(user.id, alloc); } : undefined
          });
        }),
        // +N indicator (expand) or collapse button
        hiddenCount > 0 && React.createElement('div', {
          className: 'rounded-full flex items-center justify-center cursor-pointer font-semibold ' +
            (isExpanded ? 'bg-slate-300 text-slate-500 hover:bg-slate-400' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'),
          style: {
            width: PLUS_N_SIZE,
            height: PLUS_N_SIZE,
            fontSize: isExpanded ? '11px' : '13px'
          },
          onClick: function(e) {
            e.stopPropagation();
            setExpandedAllocUserId(isExpanded ? null : user.id);
          },
          title: isExpanded ? 'Collapse' : '+' + hiddenCount + ' more projects'
        }, isExpanded ? '−' : '+' + hiddenCount)
      )
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  // Phase 4.7: Container uses height:100% - parent MUST provide proper sizing
  // SAFETY NET: HFB middle region owns scrolling; page overflow must be hidden
  var containerStyle = isFullScreen 
    ? { 
        // TRUE FULLSCREEN: Cover everything including header/subheader
        position: 'fixed', 
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999, 
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }
    : { 
        // NORMAL MODE: Component fills parent container
        // Parent in App.js MUST set: height: calc(100vh - navHeight), overflow: hidden
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden' // CRITICAL: Prevent HFB from causing page scroll
      };

  return React.createElement(DndContext, {
    sensors: sensors,
    collisionDetection: closestCenter,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel
  },
    React.createElement('div', { 
      ref: containerRef,
      style: containerStyle,
      'data-hfb-container': 'true'
    },
      React.createElement(ToastContainer, { toasts: toasts, onDismiss: dismissToast }),

      // HEADER
      React.createElement('div', { 
        className: 'flex items-center justify-between px-4 py-2 border-b border-slate-200 flex-shrink-0',
        style: { height: '52px', backgroundColor: 'white' }
      },
        React.createElement('div', { className: 'flex items-center gap-4' },
          focusedTeamId && React.createElement('button', {
            onClick: function() { setFocusedTeamId(null); },
            className: 'p-1 hover:bg-slate-100 rounded transition-colors',
            title: 'Back to all teams'
          }, React.createElement(ChevronLeft, { className: 'w-5 h-5 text-slate-500' })),
          React.createElement('span', {
            className: 'font-semibold text-slate-700',
            style: { fontSize: '14px' }
          }, focusedTeamId
            ? (activeTeams.find(function(t) { return t.id === focusedTeamId; }) || {}).name || 'Team'
            : 'Team Allocation'
          ),
          React.createElement('div', { className: 'flex items-center gap-1' },
            React.createElement('button', { 
              onClick: handlePreviousWeek, 
              className: 'p-1.5 hover:bg-slate-100 rounded-none transition-colors'
            }, React.createElement(ChevronLeft, { className: 'w-5 h-5 text-slate-500' })),
            React.createElement('div', { 
              className: 'text-center px-3 flex items-center gap-2',
              style: { minWidth: '180px' }
            },
              React.createElement(Calendar, { className: 'w-4 h-4 text-slate-400' }),
              React.createElement('span', { 
                className: 'text-sm font-medium text-slate-600' 
              }, formatWeekDisplay(currentWeekId)),
              isCurrentWeek && React.createElement('span', { 
                className: 'px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-none font-medium'
              }, 'Now')
            ),
            React.createElement('button', { 
              onClick: handleNextWeek, 
              className: 'p-1.5 hover:bg-slate-100 rounded-none transition-colors'
            }, React.createElement(ChevronRight, { className: 'w-5 h-5 text-slate-500' })),
            !isCurrentWeek && React.createElement('button', {
              onClick: handleGoToCurrentWeek,
              className: 'ml-2 px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded-none hover:bg-blue-200 font-medium'
            }, 'Today')
          ),
          isPastWeek && (isLockedWeek
            ? React.createElement('div', {
                className: 'flex items-center gap-1 text-amber-600 text-xs'
              },
                React.createElement(Lock, { className: 'w-4 h-4' }),
                React.createElement('span', null, 'Read-only')
              )
            : React.createElement('div', {
                className: 'flex items-center gap-1 text-amber-600 text-xs',
                title: 'This is a past week. As an admin you can retroactively edit assignments, allocations and targets.'
              },
                React.createElement(Shield, { className: 'w-4 h-4' }),
                React.createElement('span', null, 'Past week · admin edit')
              )
          )
        ),
        React.createElement('div', { className: 'flex items-center gap-2' },
          isLoading && React.createElement(Loader2, { className: 'w-5 h-5 animate-spin text-blue-500' }),
          // Phase 4.5: Saving indicator
          isSaving && React.createElement('div', { 
            className: 'flex items-center gap-1 text-blue-600 text-xs'
          },
            React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }),
            React.createElement('span', null, 'Saving…')
          ),
          // Canvas controls cluster: toggle + zoom out + zoom % + zoom in + reset
          React.createElement('div', { className: 'flex items-center' },
            React.createElement('button', {
              onClick: handleToggleScrollMode,
              className: 'px-2 py-1.5 text-xs border border-slate-200 ' + (scrollMode ? 'rounded-none' : 'rounded-none border-r-0') + ' hover:bg-slate-50 font-medium transition-colors bg-white text-slate-600',
              title: scrollMode ? 'Switch to Canvas mode (zoom + pan)' : 'Switch to Scroll mode'
            }, scrollMode ? 'Scroll' : 'Canvas'),
            !scrollMode && React.createElement('button', {
              onClick: handleZoomOut,
              className: 'w-7 flex items-center justify-center text-slate-600 hover:bg-slate-50 border-y border-slate-200 text-xs font-medium bg-white py-1.5',
              title: 'Zoom out (Ctrl + scroll down)'
            }, '−'),
            !scrollMode && React.createElement('span', {
              className: 'text-xs text-slate-500 min-w-[40px] text-center font-medium border-y border-slate-200 py-1.5 bg-white'
            }, Math.round(zoom * 100) + '%'),
            !scrollMode && React.createElement('button', {
              onClick: handleZoomIn,
              className: 'w-7 flex items-center justify-center text-slate-600 hover:bg-slate-50 border-y border-slate-200 text-xs font-medium bg-white py-1.5',
              title: 'Zoom in (Ctrl + scroll up)'
            }, '+'),
            !scrollMode && React.createElement('button', {
              onClick: handleResetView,
              className: 'px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-none font-medium bg-white',
              title: 'Reset view (Esc)'
            }, 'Reset')
          ),
          // Allocation visibility toggle
          React.createElement('button', {
            onClick: function() { setShowAllocations(!showAllocations); },
            className: 'flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded-none hover:bg-slate-50 font-medium transition-colors ' + (showAllocations ? 'bg-white' : 'bg-slate-100'),
            title: showAllocations ? 'Hide project allocations' : 'Show project allocations'
          },
            showAllocations 
              ? React.createElement(Eye, { className: 'w-4 h-4 text-slate-600' })
              : React.createElement(EyeOff, { className: 'w-4 h-4 text-slate-400' }),
            React.createElement('span', { className: showAllocations ? 'text-slate-600' : 'text-slate-400' }, 'Allocations')
          ),
          // Active Projects visibility toggle - directly to the right of Allocations
          React.createElement('button', {
            onClick: function() { setShowActiveProjects(!showActiveProjects); },
            className: 'flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 border-l-0 rounded-none hover:bg-slate-50 font-medium transition-colors ' + (showActiveProjects ? 'bg-white' : 'bg-slate-100'),
            title: showActiveProjects ? 'Hide Active Projects column' : 'Show Active Projects column'
          },
            React.createElement(Briefcase, { 
              className: 'w-4 h-4 ' + (showActiveProjects ? 'text-slate-600' : 'text-slate-400') 
            }),
            React.createElement('span', { className: showActiveProjects ? 'text-slate-600' : 'text-slate-400' }, 'Projects')
          ),
          // Avatar mode segmented control
          React.createElement('div', {
            className: 'flex items-center border border-slate-200 rounded-none',
            title: 'Avatar display mode'
          },
            React.createElement('button', {
              onClick: function() { handleSetAvatarMode('role'); },
              className: 'px-2 py-1.5 text-xs font-medium transition-colors ' + (avatarMode === 'role' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'),
              title: 'Initials + role color'
            }, 'Role'),
            React.createElement('button', {
              onClick: function() { handleSetAvatarMode('photo'); },
              className: 'px-2 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors ' + (avatarMode === 'photo' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'),
              title: 'Profile photo (fallback: initials)'
            }, React.createElement(User, { className: 'w-3.5 h-3.5' })),
            React.createElement('button', {
              onClick: function() { handleSetAvatarMode('personality'); },
              className: 'px-2 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors ' + (avatarMode === 'personality' ? 'bg-slate-700 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'),
              title: 'Initials + MBTI personality color'
            }, 'MBTI')
          ),
          // Model Demand overlay toggle
          React.createElement('button', {
            onClick: function() { setShowModelDemand(!showModelDemand); },
            className: 'flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded-none hover:bg-slate-50 font-medium transition-colors ' + (showModelDemand ? 'bg-amber-50 border-amber-300' : 'bg-white'),
            title: showModelDemand ? 'Hide model demand overlay' : 'Show model demand overlay'
          },
            React.createElement(AlertTriangle, { 
              className: 'w-3.5 h-3.5 ' + (showModelDemand ? 'text-amber-600' : 'text-slate-400') 
            }),
            React.createElement('span', { className: showModelDemand ? 'text-amber-700' : 'text-slate-400' }, 'Model'),
            modelDemandLoading && React.createElement(Loader2, { className: 'w-3 h-3 animate-spin text-amber-500' })
          ),
          !isLockedWeek && React.createElement('button', {
            onClick: handleOpenCopyWeekModal,
            className: 'flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded-none hover:bg-slate-50 font-medium',
            disabled: copyingWeek
          },
            React.createElement(Copy, { className: 'w-4 h-4' }),
            'Copy Week'
          ),
          React.createElement('button', {
            onClick: function() { setIsFullScreen(!isFullScreen); },
            className: 'p-1.5 hover:bg-slate-100 rounded-none',
            title: isFullScreen ? 'Exit full screen' : 'Full screen'
          }, isFullScreen 
            ? React.createElement(Minimize2, { className: 'w-5 h-5 text-slate-500' })
            : React.createElement(Maximize2, { className: 'w-5 h-5 text-slate-500' })
          )
        )
      ),

      dataErrors.length > 0 && React.createElement('div', { 
        className: 'mx-4 mt-2 flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded text-sm flex-shrink-0'
      },
        React.createElement(AlertCircle, { className: 'w-5 h-5 flex-shrink-0' }),
        React.createElement('span', null, dataErrors.join(', '))
      ),

      // Main content - split into team columns area and sticky bottom lanes
      // Phase 4.7: Restructured for proper scroll mode and sticky footer
      // Phase 4.8: Added Active Projects column as a sibling to the board
      React.createElement('div', { 
        className: 'flex-1 flex',
        style: { minHeight: 0, overflow: 'hidden' }
      },
        // Board area (team columns - scrollable/zoomable)
        React.createElement('div', { 
          className: 'flex-1 flex flex-col',
          style: { minHeight: 0, position: 'relative', overflow: 'hidden' }
        },
          // Team columns area (scrollable/zoomable) - takes remaining space above sticky footer
          React.createElement('div', { 
            className: 'flex-1 relative',
            style: { 
              minHeight: '200px',
              // Phase 4.7: Dynamic padding for measured sticky footer height
              paddingBottom: footerHeight + 'px'
            }
          },
            // Phase 4.6: SCROLL MODE - native overflow:auto, no transforms
            scrollMode ? React.createElement('div', { 
              ref: function(el) { 
                canvasRef.current = el; 
                scrollContainerRef.current = el; 
              },
              className: 'h-full',
              style: { 
                overflow: 'auto',
                cursor: 'default' // Phase 4.6: Normal cursor in scroll mode
              }
            },
              React.createElement('div', { 
                ref: boardContentRef,
                className: 'flex gap-px',
                style: { 
                  minWidth: 'max-content',
                  minHeight: boardHeight + 'px', // Phase 4.6: Full height for droppable
                  justifyContent: isFullScreen ? 'center' : 'flex-start',
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingBottom: 20
                }
              },
            displayTeams.map(function(team) {
              var teamUsers = userCategories.byTeam.get(team.id) || [];
              var supervisorIds = team.supervisorIds || [];
              var hasZeroSupervisors = supervisorIds.length === 0;
              var userCanManage = canManageTeam(team.id);

              return React.createElement(DroppableTeamColumn, {
                key: team.id,
                team: team,
                disabled: isLockedWeek || (!isAdmin && !userCanManage),
                isDragging: isDragging,
                isFullScreen: isFullScreen,
                isFocused: !!focusedTeamId,
                boardHeight: boardHeight // Phase 4.6: Pass board height
              },
                React.createElement('div', { 
                  className: 'px-3 py-2 border-b border-slate-100 flex-shrink-0',
                  style: { backgroundColor: 'white' }
                },
                  React.createElement('div', { className: 'flex items-center gap-2' },
                    React.createElement('div', { 
                      className: 'w-3 h-3 rounded-full flex-shrink-0', 
                      style: { backgroundColor: team.color || '#94a3b8' } 
                    }),
                    React.createElement('span', {
                      className: 'text-sm font-semibold text-slate-700 truncate flex-1 cursor-pointer hover:text-slate-900',
                      title: focusedTeamId ? team.name : 'Click to focus on ' + team.name,
                      onClick: function(e) { e.stopPropagation(); if (!focusedTeamId) setFocusedTeamId(team.id); }
                    }, team.name),
                    // Supervisor edit button (always visible for admins)
                    isAdmin && React.createElement('button', {
                      onClick: function(e) { e.stopPropagation(); handleOpenSupervisorModal(team); },
                      className: 'p-0.5 rounded hover:bg-slate-100 transition-colors',
                      title: 'Edit supervisors'
                    },
                      React.createElement(Shield, { className: 'w-3 h-3 text-slate-400 hover:text-slate-600' })
                    ),
                    React.createElement('span', { 
                      className: 'text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded'
                    }, teamUsers.length)
                  ),
                  // "No supervisor" warning - clickable for admins (SCROLL MODE)
                  hasZeroSupervisors && React.createElement('div', { 
                    className: 'flex items-center gap-1 text-amber-500 text-xs mt-1' + (isAdmin ? ' cursor-pointer hover:text-amber-600' : ''),
                    onClick: isAdmin ? function(e) { e.stopPropagation(); handleOpenSupervisorModal(team); } : undefined,
                    title: isAdmin ? 'Click to assign supervisors' : undefined
                  },
                    React.createElement(AlertTriangle, { className: 'w-3 h-3' }),
                    React.createElement('span', null, 'No supervisor')
                  )
                ),
                // Team members - Phase 4.6: flex-1 to fill remaining column height
                // In focused mode: dynamic grid with 1.5x spacing
                React.createElement('div', {
                  className: focusedTeamId ? 'flex-1 px-2 py-2' : 'flex-1 px-1 py-1',
                  style: focusedTeamId
                    ? {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(' + getFocusedGridColumns(teamUsers.length) + ', 1fr)',
                        gridAutoRows: 'min-content',
                        gap: '12px 8px',
                        alignContent: 'start',
                        justifyItems: 'start',
                        padding: '12px 8px',
                        minHeight: (boardHeight - 60) + 'px'
                      }
                    : { minHeight: (boardHeight - 60) + 'px' }
                },
                  teamUsers.map(function(user) {
                    return renderCompactUser(user, team.id, team.color);
                  }),
                  teamUsers.length === 0 && React.createElement('div', {
                    className: 'text-xs text-slate-400 text-center py-8'
                  }, isDragging ? 'Drop here' : 'No members')
                )
              );
            })
          )
          ) :
          // Phase 4.6: CANVAS MODE - zoom + pan with transforms
          React.createElement('div', { 
            ref: canvasRef,
            className: 'overflow-hidden h-full',
            style: { 
              // Phase 4.6: Only show grab cursor in canvas mode
              cursor: isDragging ? 'default' : (isPanningRef.current ? 'grabbing' : 'grab')
            },
            onWheel: handleCanvasWheel,
            onMouseDown: handleCanvasMouseDown
          },
            React.createElement('div', { 
              ref: boardContentRef,
              className: 'flex gap-px',
              style: { 
                minWidth: 'max-content',
                minHeight: boardHeight + 'px', // Phase 4.6: Full height for droppable
                justifyContent: isFullScreen ? 'center' : 'flex-start',
                paddingLeft: 24,
                paddingRight: 24,
                transform: 'translate(' + panOffset.x + 'px, ' + panOffset.y + 'px) scale(' + zoom + ')',
                transformOrigin: '0 0',
                transition: isPanningRef.current ? 'none' : 'transform 0.1s ease-out'
              }
            },
            displayTeams.map(function(team) {
              var teamUsers = userCategories.byTeam.get(team.id) || [];
              var supervisorIds = team.supervisorIds || [];
              var hasZeroSupervisors = supervisorIds.length === 0;
              var userCanManage = canManageTeam(team.id);

              return React.createElement(DroppableTeamColumn, {
                key: team.id,
                team: team,
                disabled: isLockedWeek || (!isAdmin && !userCanManage),
                isDragging: isDragging,
                isFullScreen: isFullScreen,
                isFocused: !!focusedTeamId,
                boardHeight: boardHeight // Phase 4.6: Pass board height
              },
                React.createElement('div', { 
                  className: 'px-3 py-2 border-b border-slate-100 flex-shrink-0',
                  style: { backgroundColor: 'white' }
                },
                  React.createElement('div', { className: 'flex items-center gap-2' },
                    React.createElement('div', { 
                      className: 'w-3 h-3 rounded-full flex-shrink-0', 
                      style: { backgroundColor: team.color || '#94a3b8' } 
                    }),
                    React.createElement('span', {
                      className: 'text-sm font-semibold text-slate-700 truncate flex-1 cursor-pointer hover:text-slate-900',
                      title: focusedTeamId ? team.name : 'Click to focus on ' + team.name,
                      onClick: function(e) { e.stopPropagation(); if (!focusedTeamId) setFocusedTeamId(team.id); }
                    }, team.name),
                    // Supervisor edit button (always visible for admins) - CANVAS MODE
                    isAdmin && React.createElement('button', {
                      onClick: function(e) { e.stopPropagation(); handleOpenSupervisorModal(team); },
                      className: 'p-0.5 rounded hover:bg-slate-100 transition-colors',
                      title: 'Edit supervisors'
                    },
                      React.createElement(Shield, { className: 'w-3 h-3 text-slate-400 hover:text-slate-600' })
                    ),
                    React.createElement('span', { 
                      className: 'text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded'
                    }, teamUsers.length)
                  ),
                  // "No supervisor" warning - clickable for admins (CANVAS MODE)
                  hasZeroSupervisors && React.createElement('div', { 
                    className: 'flex items-center gap-1 text-amber-500 text-xs mt-1' + (isAdmin ? ' cursor-pointer hover:text-amber-600' : ''),
                    onClick: isAdmin ? function(e) { e.stopPropagation(); handleOpenSupervisorModal(team); } : undefined,
                    title: isAdmin ? 'Click to assign supervisors' : undefined
                  },
                    React.createElement(AlertTriangle, { className: 'w-3 h-3' }),
                    React.createElement('span', null, 'No supervisor')
                  )
                ),
                // Team members - Phase 4.6: flex-1 to fill remaining column height
                // In focused mode: dynamic grid with 1.5x spacing
                React.createElement('div', {
                  className: focusedTeamId ? 'flex-1 px-2 py-2' : 'flex-1 px-1 py-1',
                  style: focusedTeamId
                    ? {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(' + getFocusedGridColumns(teamUsers.length) + ', 1fr)',
                        gridAutoRows: 'min-content',
                        gap: '12px 8px',
                        alignContent: 'start',
                        justifyItems: 'start',
                        padding: '12px 8px',
                        minHeight: (boardHeight - 60) + 'px'
                      }
                    : { minHeight: (boardHeight - 60) + 'px' }
                },
                  teamUsers.map(function(user) {
                    return renderCompactUser(user, team.id, team.color);
                  }),
                  teamUsers.length === 0 && React.createElement('div', {
                    className: 'text-xs text-slate-400 text-center py-8'
                  }, isDragging ? 'Drop here' : 'No members')
                )
              );
            })
          )
          )
        ),

        // Phase 4.7: Bottom zones - STICKY at bottom, always visible
        // Hidden when in focused team view mode
        // Uses ref for dynamic height measurement
        React.createElement('div', {
          ref: stickyFooterRef,
          className: 'flex-shrink-0 border-t border-slate-200 bg-white',
          style: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            paddingLeft: 24,
            paddingRight: 24,
            // SAFETY NET: Solid background so content doesn't show through
            backgroundColor: 'white',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.05)',
            display: focusedTeamId ? 'none' : 'block'
          }
        },
          // Unassigned + Floating Zones — shared collapse toggle, side-by-side row
          React.createElement('div', {
            style: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start'
            }
          },
            // Single collapse toggle button on the left
            React.createElement('button', {
              style: {
                background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px 8px 0',
                display: 'flex', alignItems: 'center', color: '#94a3b8',
                flexShrink: 0, alignSelf: 'flex-start'
              },
              title: poolsCollapsed ? 'Expand Unassigned & Floating' : 'Collapse Unassigned & Floating',
              onClick: function() { setPoolsCollapsed(function(prev) { return !prev; }); }
            },
              React.createElement(poolsCollapsed ? ChevronUp : ChevronDown, { className: 'w-4 h-4' })
            ),

            // Zones container
            React.createElement('div', {
              style: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0px' }
            },
              // Unassigned Zone (left)
              React.createElement('div', {
                style: { flex: '1 1 280px', minWidth: 0 }
              },
                React.createElement(DroppableZone, {
                  zoneId: 'pool:unassigned',
                  disabled: isLockedWeek,
                  isDragging: isDragging
                },
                  React.createElement('div', { className: 'px-3 py-2' },
                    React.createElement('div', { className: 'flex items-center gap-2' + (poolsCollapsed ? '' : ' mb-2') },
                      React.createElement('span', { className: 'text-sm font-semibold text-slate-600 uppercase tracking-wide' }, 'Unassigned'),
                      React.createElement('span', { className: 'text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded' }, userCategories.unassigned.length),
                      isLockedWeek && React.createElement(Lock, { className: 'w-3 h-3 text-amber-500' })
                    ),
                    // Collapsible content
                    React.createElement('div', {
                      style: {
                        maxHeight: poolsCollapsed ? '0px' : '300px',
                        overflow: poolsCollapsed ? 'hidden' : 'visible',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.25s ease-in-out',
                        opacity: poolsCollapsed ? 0 : 1
                      }
                    },
                      React.createElement('div', { className: 'flex flex-wrap gap-3' },
                        userCategories.unassigned.map(function(user) {
                          return renderCompactUser(user, null, null);
                        }),
                        userCategories.unassigned.length === 0 && React.createElement('span', {
                          className: 'text-sm text-slate-400 italic'
                        }, isDragging ? 'Drop to unassign' : 'All members assigned')
                      )
                    )
                  )
                )
              ),

              // Vertical divider between lanes
              React.createElement('div', {
                style: { width: '1px', alignSelf: 'stretch', backgroundColor: '#e2e8f0', flexShrink: 0 }
              }),

              // Floating Zone (right)
              React.createElement('div', {
                style: { flex: '1 1 280px', minWidth: 0 }
              },
                React.createElement(DroppableZone, {
                  zoneId: 'pool:floating',
                  disabled: isLockedWeek,
                  isDragging: isDragging
                },
                  React.createElement('div', { className: 'px-3 py-2' },
                    React.createElement('div', { className: 'flex items-center gap-2' + (poolsCollapsed ? '' : ' mb-2') },
                      React.createElement('span', { className: 'text-sm font-semibold text-slate-600 uppercase tracking-wide' }, 'Floating'),
                      React.createElement('span', { className: 'text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded' }, userCategories.floating.length),
                      React.createElement('span', { className: 'text-xs text-slate-400 italic ml-2' }, '(Mgmt / Support)')
                    ),
                    // Collapsible content
                    React.createElement('div', {
                      style: {
                        maxHeight: poolsCollapsed ? '0px' : '300px',
                        overflow: poolsCollapsed ? 'hidden' : 'visible',
                        transition: 'max-height 0.3s ease-in-out, opacity 0.25s ease-in-out',
                        opacity: poolsCollapsed ? 0 : 1
                      }
                    },
                      React.createElement('div', { className: 'flex flex-wrap gap-3' },
                        userCategories.floating.map(function(user) {
                          return renderCompactUser(user, null, null);
                        }),
                        userCategories.floating.length === 0 && React.createElement('span', {
                          className: 'text-sm text-slate-400 italic'
                        }, isDragging ? 'Drop to set as floating' : 'No floating people')
                      )
                    )
                  )
                )
              )
            )
          )
        )
        ), // Close board area flex-col container (Team columns area + sticky footer)

        // ACTIVE PROJECTS COLUMN - Phase 4.8: Minimal narrow column on the right
        showActiveProjects && React.createElement('div', {
          className: 'flex-shrink-0 border-l border-slate-200 bg-white flex flex-col',
          style: { 
            width: '180px',
            minWidth: '180px'
          }
        },
          // Minimal column header - just loading indicator if needed
          React.createElement('div', {
            className: 'px-2 py-1.5 border-b border-slate-100 flex items-center justify-end flex-shrink-0',
            style: { minHeight: '28px' }
          },
            roleTargetsLoading && React.createElement(Loader2, { 
              className: 'w-3 h-3 animate-spin text-slate-400' 
            })
          ),
          // Column content - scrollable list of active projects
          React.createElement('div', {
            className: 'flex-1 overflow-hidden'
          },
            React.createElement(ActiveProjectsColumn, {
              projects: projects,
              users: users,
              allocations: allocations,
              targets: roleTargets,
              onTargetChange: handleRoleTargetChange,
              canEditTargets: canEditRoleTargets,
              isPastWeek: isLockedWeek,
              maxHeight: '100%',
              showModelDemand: showModelDemand,
              modelDemand: currentWeekModelDemand,
              modelDemandNoData: modelDemandNoData,
              modelDemandError: modelDemandError,
              modelDemandRunInfo: modelDemandRunInfo
            })
          )
        )
      ), // Close main content flex container

      // DRAG OVERLAY - clean avatar only, no card/outline
      React.createElement(DragOverlay, { dropAnimation: { duration: 200, easing: 'ease-out' } },
        activeDragUser && React.createElement(CompactUserAvatar, {
          user: activeDragUser,
          teamColor: (function() {
            var assignment = getUserTeamAssignment(activeDragUser.id);
            var team = assignment ? getTeamById(assignment.teamId) : null;
            return team ? team.color : null;
          })(),
          size: USER_AVATAR_SIZE,
          isSupervisor: isUserSupervisorOfAnyTeam(activeDragUser.id),
          avatarMode: avatarMode,
          profileData: profileCache[activeDragUser.id]
        })
      ),

      // CONFIRM DIALOG
      confirmDialog && React.createElement('div', { 
        className: 'fixed inset-0 bg-black/50 flex items-center justify-center',
        style: { zIndex: 99998 }
      },
        React.createElement('div', { className: 'bg-white rounded-lg p-5 w-full max-w-sm mx-4 shadow-xl' },
          React.createElement('div', { className: 'flex items-start gap-3 mb-4' },
            React.createElement(AlertTriangle, { className: 'w-6 h-6 text-amber-500 flex-shrink-0' }),
            React.createElement('div', null,
              React.createElement('h3', { className: 'font-semibold text-base text-slate-800' }, 'Confirm Move'),
              React.createElement('p', { className: 'text-sm text-slate-600 mt-1' }, confirmDialog.message)
            )
          ),
          React.createElement('div', { className: 'flex justify-end gap-2' },
            React.createElement('button', {
              onClick: confirmDialog.onCancel,
              className: 'px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded transition-colors'
            }, 'Cancel'),
            React.createElement('button', {
              onClick: confirmDialog.onConfirm,
              className: 'px-4 py-2 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors font-medium'
            }, 'Move & Clear')
          )
        )
      ),

      // COPY WEEK MODAL
      showCopyWeekModal && React.createElement('div', { 
        className: 'fixed inset-0 bg-black/50 flex items-center justify-center',
        style: { zIndex: 99998 }
      },
        React.createElement('div', { className: 'bg-white rounded-lg p-5 w-full max-w-sm mx-4 shadow-xl' },
          React.createElement('h3', { className: 'text-base font-semibold mb-2 text-slate-800' }, 'Copy from Previous Week'),
          React.createElement('p', { className: 'text-sm text-slate-600 mb-4' }, 
            'Copy to ', React.createElement('strong', null, formatWeekDisplay(currentWeekId))
          ),
          copyResult && !copyResult.success && React.createElement('div', { className: 'mb-3 p-2 bg-red-50 text-red-600 border border-red-200 rounded text-sm' },
            copyResult.errors ? copyResult.errors.join(', ') : 'Unknown error'
          ),
          React.createElement('div', { className: 'space-y-2' },
            [-1, -2, -3, -4].map(function(delta) {
              var sourceWeek = getAdjacentWeek(currentWeekId, delta);
              return React.createElement('button', {
                key: sourceWeek,
                onClick: function() { handleCopyWeek(sourceWeek); },
                disabled: copyingWeek,
                className: 'w-full text-left px-4 py-2.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 transition-colors'
              },
                React.createElement('div', { className: 'text-sm font-medium text-slate-700' }, formatWeekDisplay(sourceWeek))
              );
            })
          ),
          React.createElement('div', { className: 'flex justify-end mt-4' },
            React.createElement('button', {
              onClick: function() { setShowCopyWeekModal(false); setCopyResult(null); },
              disabled: copyingWeek,
              className: 'px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50 transition-colors'
            }, 'Cancel')
          )
        )
      ),

      // SUPERVISOR EDIT MODAL
      supervisorModal && React.createElement('div', { 
        className: 'fixed inset-0 bg-black/50 flex items-center justify-center',
        style: { zIndex: 99998 },
        onClick: handleCloseSupervisorModal
      },
        React.createElement('div', { 
          className: 'bg-white rounded-lg p-5 w-full max-w-md mx-4 shadow-xl',
          onClick: function(e) { e.stopPropagation(); }
        },
          React.createElement('div', { className: 'flex items-center justify-between mb-4' },
            React.createElement('h3', { className: 'text-base font-semibold text-slate-800' }, 
              'Edit Supervisors - ' + supervisorModal.teamName
            ),
            React.createElement('button', {
              onClick: handleCloseSupervisorModal,
              className: 'p-1 hover:bg-slate-100 rounded transition-colors'
            },
              React.createElement(X, { className: 'w-5 h-5 text-slate-500' })
            )
          ),
          React.createElement('p', { className: 'text-sm text-slate-600 mb-4' }, 
            'Select team members to assign as supervisors. Supervisors can approve time-off requests for their team.'
          ),
          
          // User selection list
          React.createElement('div', { 
            className: 'border border-slate-200 rounded-lg max-h-64 overflow-y-auto mb-4'
          },
            users
              .filter(function(u) { return u.status !== 'inactive'; })
              .sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); })
              .map(function(user) {
                var isSelected = selectedSupervisors.includes(String(user.id));
                return React.createElement('div', {
                  key: user.id,
                  className: 'flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0',
                  onClick: function() { handleToggleSupervisor(user.id); }
                },
                  React.createElement('input', {
                    type: 'checkbox',
                    checked: isSelected,
                    onChange: function() {},
                    className: 'w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500'
                  }),
                  React.createElement('div', { 
                    className: 'w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold',
                    style: { backgroundColor: brioRoleColors[user.role] || '#64748b' }
                  },
                    (user.name || 'U').substring(0, 2).toUpperCase()
                  ),
                  React.createElement('div', { className: 'flex-1' },
                    React.createElement('div', { className: 'text-sm font-medium text-slate-700' }, user.name),
                    React.createElement('div', { className: 'text-xs text-slate-500' }, user.role || 'No role')
                  ),
                  isSelected && React.createElement(Shield, { className: 'w-4 h-4 text-amber-500' })
                );
              })
          ),
          
          // Selected count
          React.createElement('div', { className: 'text-sm text-slate-500 mb-4' },
            selectedSupervisors.length + ' supervisor(s) selected'
          ),
          
          // Action buttons
          React.createElement('div', { className: 'flex justify-end gap-2' },
            React.createElement('button', {
              onClick: handleCloseSupervisorModal,
              disabled: savingSupervisors,
              className: 'px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded transition-colors disabled:opacity-50'
            }, 'Cancel'),
            React.createElement('button', {
              onClick: handleSaveSupervisors,
              disabled: savingSupervisors,
              className: 'px-4 py-2 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 flex items-center gap-2'
            },
              savingSupervisors && React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }),
              savingSupervisors ? 'Saving...' : 'Save Supervisors'
            )
          )
        )
      )
    )
  );
}
